import { parseCaClaimFeeQuote, type CaClaimFeeQuoteInput, type CaClaimFeeQuoteResult, type CaFeeCalculation } from "./claim-fees";
import { parseEncounterQuote, type EncounterQuoteInput, type EncounterQuoteResult } from "./encounter-fees";
/** Paper claim family; clearinghouse transport is selected by the server. */
export type ClaimForm = "cms1500" | "ub04" | "ada" | "ncpdp";
/** JSON fields validated by the server's form-specific contract. */
export type BillFormValue = string | number | boolean | null | BillFormValue[] | { [key: string]: BillFormValue };
export type BillFormData = {
  institutional?: { [key: string]: BillFormValue };
  dental?: { [key: string]: BillFormValue };
  pharmacy?: { [key: string]: BillFormValue };
};
/** Service-line metadata, including revenue, tooth, and prescription details. */
export type BillLineFormData = BillFormData;

export type { CaClaimFeeQuoteInput, CaClaimFeeQuoteResult, CaClaimLineQuote, CaFeeCalculation, CaFeeCitation, CaClaimEditFinding, CaClaimFeeFinding } from "./claim-fees";
export { normalizeRfaFax, rfaAuthorizationDestinations, rfaAuthorizationGuidance } from "./rfa-directory";
export type { RfaAuthorizationDestinationOption } from "./rfa-directory";
export const DEFAULT_API_BASE_URL = "https://app.mindbill.org";
export const DEFAULT_SESSION_ENDPOINT = "/api/mindbill/session";
export { parseSecondReviewCorrection, type CorrectionDraft } from "./second-review-corrections";

export type BillReviewDocumentType =
  | "final_report"
  | "letter_of_attestation"
  | "proof_of_service"
  | "form_122"
  | "return_to_work_voucher"
  | "w9"
  | "medical_records"
  | "appeal"
  | "other";

export type BillReviewBillingProvider = {
  name: string;
  taxId: string;
  taxIdType?: "EIN" | "SSN";
  taxIdConfigured?: boolean;
  taxIdLast4?: string;
  npi: string;
  billType: "Professional" | "Institutional" | "Dental" | "Pharmacy";
  phone?: string;
  billingStreet?: string;
  billingCity?: string;
  billingState?: string;
  billingZip?: string;
};

export type BillReviewClinician = {
  /** Canonical opaque provider ID, when available. */
  id?: string;
  name: string;
  specialty: string;
  npi: string;
  taxonomy?: string;
  licenseNumber?: string;
  licenseState?: string;
  signaturePng?: string;
  signatureKey?: string;
  isQME?: boolean;
  isAME?: boolean;
  email?: string;
  active?: boolean;
};

export type BillReviewLocation = {
  billingProviderId?: string;
  name: string;
  nickname?: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  county?: string;
  posCode?: string;
  isPrimary?: boolean;
  active?: boolean;
};

/** Verified, server-provided fee explanation. Display strings only; never recalculate fees from these values. */
export type BillLineFeeBreakdown = {
  method: string;
  inputs: Array<{ label: string; value: string }>;
  steps: Array<{ label: string; value: string }>;
  notes: string[];
};

export type BillReviewLineItem = {
  formData?: BillLineFormData;
  drug?: BilledDrug;
  id?: string;
  code: string;
  modifiers: string[];
  units: number;
  charge: number;
  feeSchedule?: number;
  /** Omitted when no verified fee explanation is available for this saved line. */
  pricing?: { breakdown?: BillLineFeeBreakdown };
  serviceDate?: string | null;
  serviceDateEnd?: string | null;
  diagnosisPointers?: number[];
};

export type BillReviewAttachment = {
  id: string;
  filename: string;
  description?: string | null;
  documentType: string;
  reportType?: string | null;
  source?: string;
  addedAt?: string;
  contentUrl?: string;
};

/** One selectable payer (subpayor) under a claims administrator. */
export type BillReviewPayerOption = {
  id: string;
  label: string;
  /** Directory-provided default marker. Interactive forms still require an explicit choice. */
  default?: boolean;
  active?: boolean;
  aliases?: string[];
  hint?: string | null;
  affiliatedEntities?: string[];
  route?: string | null;
  optionType?: string | null;
  deliveryType?: string | null;
  clearinghouse?: string | null;
  payerId?: string | null;
  /** Original directory route when live delivery uses a replacement clearinghouse. */
  sourceClearinghouse?: string | null;
  /** Original directory payer ID when live delivery uses a replacement route. */
  sourcePayerId?: string | null;
  clearinghousePayerIds?: Record<string, string>;
  preferredClearinghouse?: string | null;
};

export type BillReviewPayer = {
  id: string;
  name: string;
  hasElectronic?: boolean;
  states?: string[];
  confidence?: "high" | "medium" | "directory";
  recommended?: boolean;
  signals?: Array<{
    kind: "name" | "claim_number";
    state: "match" | "warning";
    label: string;
  }>;
  /** True when a bill for this claims administrator must name one of `payers`. */
  payerSelectionRequired?: boolean;
  /** Selectable payers (subpayors) under this claims administrator. */
  payers?: BillReviewPayerOption[];
  aliases?: string[];
  affiliatedEntities?: string[];
  claimNumberHint?: string | null;
  claimNumberPatterns?: BillClaimsAdministratorPattern[];
  route?: string | null;
};

/** A directory suggestion derived from host-provided evidence. Suggestions are never auto-applied. */
export type BillReviewPayerSuggestion = BillReviewPayer & {
  deterministic: boolean;
  reason: string;
  /** A uniquely proven routing payer under a multi-payer claims administrator. */
  selectedPayerId?: string;
};

/** Host-system evidence shown alongside claims-administrator suggestions. */
export type BillClaimsAdministratorSource = {
  source: "eams" | "report" | string;
  label: string;
  name: string;
  url?: string;
};

/**
 * The directory-marked default payer option, or null when none is marked.
 * Interactive forms should still require an explicit routing-payer choice.
 */
export function defaultBillReviewPayerOption(
  payer: Pick<BillReviewPayer, "payerSelectionRequired" | "payers">,
): BillReviewPayerOption | null {
  if (!payer.payerSelectionRequired) return null;
  return payer.payers?.find((option) => option.default) ?? null;
}

export type BillPostalPlace = { city: string; state: string };
export type BillDiagnosisCode = { code: string; description: string };

export type BillReviewFeatures = {
  authorizationNumber?: boolean;
  serviceDateRange?: boolean;
  wcabNumber?: boolean;
  codingPresets?: boolean;
};

export type BillReviewClaimPatternStatus = {
  state: "match" | "warning" | "unknown";
  label: string;
  detail?: string;
  suggestion?: string;
};

export type BillReviewData = {
  options?: Pick<OrganizationProfileData, "billingProviders" | "renderingProviders" | "locations">;
  bill: {
    claimForm?: ClaimForm;
    formData?: BillFormData;
    id: string;
    billNumber: string | number;
    status: string;
    transmissionState?: string;
    billingMode: "med_legal" | "professional";
    dos: string;
    dosEnd?: string | null;
    authorizationNumber?: string | null;
    billingSnapshot?: {
      billingProvider?: BillReviewBillingProvider;
      renderingProvider?: BillReviewClinician;
      placeOfService?: BillReviewLocation;
    } | null;
    lineItems: BillReviewLineItem[];
    attachments: BillReviewAttachment[];
    totalCharge: number;
    totalPaid: number;
    balanceDue: number;
  };
  patient: {
    /** Canonical opaque patient ID, when available. */
    id?: string;
    name: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    dob?: string;
    phone?: string;
    address?: {
      line1?: string;
      city?: string;
      state?: string;
      postalCode?: string;
    };
  };
  injury: {
    claimNumber?: string;
    employer?: string;
    doi?: string;
    injuryEndDate?: string;
    cumulativeTrauma?: boolean;
    adjNumber?: string;
    medicalProviderNetworkId?: string | null;
    claimsAdminId?: string;
    claimsAdminName?: string;
    injuryDescription?: string;
    /** Diagnosis codes enriched with their human-readable ICD-10 descriptions. */
    diagnoses?: BillDiagnosisCode[];
    /** @deprecated Prefer diagnoses so users can understand each code. */
    diagnosisCodes?: string[];
    claimPatternStatus?: BillReviewClaimPatternStatus;
  };
};

export type BillReviewSaveInput = {
  formData?: BillFormData;
  claimsAdminId: string;
  /** The chosen payer (subpayor) when the claims administrator requires payer selection. */
  payerId?: string;
  patientOverrides?: {
    firstName: string;
    middleName?: string;
    lastName: string;
    dob?: string;
  };
  injuryOverrides?: {
    claimNumber?: string;
    employer?: string;
    doi?: string;
    injuryEndDate?: string;
    cumulativeTrauma?: boolean;
    adjNumber?: string;
    medicalProviderNetworkId?: string | null;
  };
  dos: string;
  dosEnd?: string | null;
  authorizationNumber?: string | null;
  billingProvider?: BillReviewBillingProvider;
  renderingProvider?: BillReviewClinician;
  placeOfService?: BillReviewLocation;
  lineItems: Array<{
    formData?: BillLineFormData;
    drug?: BilledDrug;
    id?: string;
    code: string;
    modifiers: string[];
    units: number;
    charge?: number;
    serviceDate?: string | null;
    serviceDateEnd?: string | null;
    diagnosisPointers?: number[];
  }>;
};

export type BillSubmissionRoute = "ebill" | "fax" | "mail" | "email";
export type BillDeliveryOption = {
  route: BillSubmissionRoute;
  label: string;
  detail: string;
  fallback: boolean;
  confidence: string;
  payerName: string;
  target?: string;
  chKey?: string;
  payerId?: string;
  printAndMail?: boolean;
  costUsd?: number;
};
export type BillDeliveryOptions = {
  payerName: string;
  recommended: BillDeliveryOption;
  options: BillDeliveryOption[];
  contacts: {
    faxNumber?: string | null;
    claimsEmail?: string | null;
    portalUrl?: string | null;
    mailingAddress?: string | null;
    adjusterName?: string | null;
    adjusterPhone?: string | null;
    adjusterEmail?: string | null;
  };
};
export type BillActorInput = {
  /** Human-readable name shown for user-initiated audit history. */
  actorName?: string;
};

export type AddBillNoteInput = BillActorInput & {
  note: string;
};

export type SubmitBillInput = BillActorInput & {
  route: BillSubmissionRoute;
  destination?: {
    faxNumber?: string;
    email?: string;
    mailingAddress?: string;
  };
  attention?: string;
  subject?: string;
  note?: string;
  /** CC recipients for the email route (at most 10). */
  cc?: string[];
};
export type BillLifecycleActionId =
  | "resubmit"
  | "second_review"
  | "independent_bill_review"
  | "view_eor"
  | "post_payment"
  | "close"
  | "reopen"
  | "send_duplicate"
  | "report_bill_status"
  | "submit_new_bill";

export type BillLifecycleAction = {
  id: BillLifecycleActionId;
  label: string;
  enabled: boolean;
  primary?: boolean;
  reason?: string;
};

export type BillEorDocument = {
  id: string;
  filename: string;
  description: string | null;
  addedAt: string;
  contentUrl: string;
};

export type BillActivityRecord = {
  id: string;
  type: string;
  createdAt: string;
  title?: string;
  description: string;
  actor: string | null;
  delivery: BillSubmissionRoute | null;
  amount: number | null;
  accepted: boolean | null;
  stcCategory: string | null;
};

/** One user-facing bill history row (Date / Action / User / Details).
 *  Server-presented: the wording is authored by MindBill so every surface (MindBill
 *  app, React, Angular, embeds) reads identically. */
export type BillHistoryEntry = {
  id: string;
  /** Immutable bill attempt that produced this event. Present on unified chain history. */
  attemptId?: string;
  /** ISO timestamp for ordering and the Date column. */
  date: string;
  /** Short Action label — "Original Bill", "277 Accept", "Payment", "Note", … */
  action: string;
  kind:
    | "created"
    | "submission"
    | "ack"
    | "eor"
    | "payment"
    | "review"
    | "close"
    | "reopen"
    | "note"
    | "portal"
    | "task"
    | "system";
  /** Who acted: a biller name, MindBill automation, or the responding clearinghouse. */
  actor: string | null;
  /** The one-line Details column. */
  summary: string;
  /** Drives row highlighting: submissions are pale blue, notes pale yellow. */
  tone: "submission" | "note" | "neutral" | "problem" | "success";
  amount?: number;
  /** Expandable content; absent → the row is not expandable. */
  details?: {
    rows?: Array<{ label: string; value: string }>;
    documents?: Array<{ id: string; filename: string }>;
    complianceDueDates?: Array<{ date: string; text: string }>;
    codes?: Array<{ code: string; text: string }>;
    text?: string;
  };
};

/** One immutable transmission attempt within a logical bill chain. */
export type BillAttemptSummary = {
  /** Unique transmission identity. Draft attempts fall back to the bill record ID. */
  id: string;
  /** Underlying bill record. Multiple transmissions may share this value. */
  billId?: string;
  submissionId?: string;
  billNumber?: string | number;
  label: string;
  deliveryLabel?: string;
  sentAt?: string;
  ackLabel?: string;
  ackAt?: string;
  status?: string;
  complianceLabel?: string;
  complianceAt?: string;
  /** Server-resolved payer outcome; no client-side deadline inference. */
  outcomeLabel?: string;
  outcomeAt?: string;
  outcomeDateLabel?: string;
  outcomeWorkingDays?: number;
  /** The latest attempt that receives lifecycle mutations. */
  isCurrent: boolean;
};

export type BillPaymentRecord = {
  id: string;
  method: "check" | "eft";
  checkNumber: string;
  status: string | null;
  depositDate: string | null;
  checkReceived: boolean | null;
  receivedDate: string | null;
  amount: number;
  principalAmount: number;
  feeAmount: number | null;
  feeReason: string | null;
  source: "paper" | "835" | "portal";
  postedAt: string;
  updatedAt: string | null;
  note: string | null;
};

export type BillRemittanceSummary = {
  billedAmount: number;
  expectedAmount: number;
  payerAllowedAmount: number | null;
  payerReportedPaid: number | null;
  postedPrincipal: number;
  postedAdditional: number;
  totalPostedCash: number;
  balanceDue: number;
  denialReason: string | null;
};

export type BillLifecycleDelivery = {
  payerName: string;
  contacts: BillDeliveryOptions["contacts"];
  /**
   * Partner-safe directory details. `id` is an opaque MindBill directory ID;
   * Values are safe, partner-facing directory details; internal database identifiers are opaque.
   */
  directory?: BillClaimsAdministratorDirectory | null;
};

export type BillClaimsAdministratorContact = {
  location?: string | null;
  method?: "fax" | "email";
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  fax?: string | null;
  portalUrl?: string | null;
  address?: string | null;
  note?: string | null;
};

export type BillClaimsAdministratorMailingAddress = {
  company?: string | null;
  address: string;
  notes?: string | null;
  submissionTypes?: string[];
  billTreatmentTypes?: string[];
};

export type BillClaimsAdministratorPattern = {
  length?: number | string | null;
  pattern: string;
  example?: string | null;
  matches?: boolean | null;
};

export type BillClaimsAdministratorPayer = {
  name: string;
  route?: string | null;
  aliases?: string[];
  hint?: string | null;
  affiliatedEntities?: string[];
  optionType?: string | null;
  deliveryType?: string | null;
  clearinghouse?: string | null;
  payerId?: string | null;
  sourceClearinghouse?: string | null;
  sourcePayerId?: string | null;
  clearinghousePayerIds?: Record<string, string>;
  preferredClearinghouse?: string | null;
};

export type BillReviewPayerPage = {
  results: BillReviewPayer[];
  suggestions?: BillReviewPayerSuggestion[];
  total: number;
  nextOffset?: number;
  recommendedId?: string;
};

export type BillReviewPayerListInput = {
  query?: string;
  claimNumber?: string;
  sourceClaimsAdministratorName?: string;
  employerName?: string;
  limit?: number;
  offset?: number;
};

export type BillClaimsAdministratorAuthorizationStatus = "central_fax" | "central_email" | "claim_handling_location_routes" | "adjuster_specific_required" | "daisybill_unverified" | "profile_not_published";

export type BillClaimsAdministratorDirectory = {
  authorizationStatus?: BillClaimsAdministratorAuthorizationStatus;
  authorizationSource?: { url: string; observedAt: string };
  id?: string;
  name?: string;
  type?: string | null;
  description?: string | null;
  website?: string | null;
  aliases?: string[];
  affiliatedEntities?: string[];
  hours?: string | null;
  claimNumberHint?: string | null;
  billProcessingWorkflow?: string | null;
  billProcessingWorkflowNotes?: string | null;
  telephoneNumbers?: string[];
  emailAddresses?: string[];
  webPortals?: string[];
  payers?: BillClaimsAdministratorPayer[];
  authorizationNotice?: string | null;
  billReview?: BillClaimsAdministratorContact[];
  authorization?: BillClaimsAdministratorContact[];
  mailingAddresses?: BillClaimsAdministratorMailingAddress[];
  claimNumberPatterns?: BillClaimsAdministratorPattern[];
};

/** End-user-safe details explaining why the latest submission was rejected. */
export type BillRejectionIssue = {
  code?: string | null;
  description: string;
  /** Bill-submission field paths implicated by this issue. */
  fieldPaths?: string[];
};

export type BillRejection = {
  reason: string;
  code?: string | null;
  issues?: BillRejectionIssue[];
  receivedAt?: string | null;
  source?: string | null;
};

export type BillTeamNote = { id: string; body: string; author: string; createdAt: string; pinned: boolean };

export type BillCourtesyCopyInput = {
  to: string[];
  cc?: string[];
  subject: string;
  bodyText: string;
  includeCms1500?: boolean;
  documentIds?: string[];
};
export type BillCourtesyCopyPreview = {
  filename: string; documentCount: number; packetHash: string; pdfBase64: string;
  environment: "sandbox" | "live";
};
export type BillCourtesyCopyResult = { ok: boolean; sent: boolean; simulated?: boolean; dryRun?: boolean; messageId?: string };

/** Historical financial balances are unavailable, not zero. */
export type HistoricalBillReviewData = Omit<BillReviewData, "bill"> & {
  bill: Omit<BillReviewData["bill"], "totalPaid" | "balanceDue"> & {
    totalPaid: number | null;
    balanceDue: number | null;
  };
};
export type BillSubmissionDetail = {
  attemptId: string;
  billId: string;
  source: "submission_snapshot" | "bill_record" | "unavailable";
  capturedAt?: string | null;
  detail: HistoricalBillReviewData | null;
  /** Exact retained files for this attempt; never regenerated from current bill values. */
  artifacts?: Array<{ id: string; label: string; kind: "submitted_attachment" | "submitted_edi"; contentType: string }>;
};

export type BillLifecycleData = BillReviewData & {
  environment: "sandbox" | "live";
  lifecycle: {
    state: string;
    nativeStatus: string;
    submittedAt?: string | null;
    agingDays?: number | null;
    updatedAt?: string | null;
    actions: BillLifecycleAction[];
  };
  eors: BillEorDocument[];
  activity: BillActivityRecord[];
  /** Presented history rows. Optional: absent when the API
   *  predates the presented-history rollout — fall back to `activity`. */
  history?: BillHistoryEntry[];
  /** Canonical team notes, shared with the MindBill app. Absent on older APIs. */
  notes?: BillTeamNote[];
  /** Immutable submissions that share this bill's unified history. */
  attempts?: BillAttemptSummary[];
  /** Per-attempt details. Older APIs may omit this; never substitute the current bill. */
  submissionDetails?: BillSubmissionDetail[];
  payments: BillPaymentRecord[];
  remittance: BillRemittanceSummary;
  delivery: BillLifecycleDelivery;
  rejection?: BillRejection | null;
};

export type BillLifecycleSession = {
  token: string;
  expiresAt?: string;
  apiBaseUrl?: string;
};

export type BillLifecycleSessionRequest = {
  signal: AbortSignal;
};

export type BillLifecycleSessionProvider = (
  request: BillLifecycleSessionRequest,
) => Promise<BillLifecycleSession>;

export type CloseBillInput = BillActorInput & { reason: string };
export type ReopenBillInput = BillActorInput & { reason: string };
export type PostBillPaymentInput = BillActorInput & {
  amount: number;
  penaltyAmount?: number;
  interestAmount?: number;
  method: "check" | "eft";
  checkNumber?: string;
  depositDate: string;
  note?: string;
};
export type SecondReviewLineInput = {
  lineItemId: string;
  reason: string;
  /** SBR-1 box for this service line. Omitted leaves the box blank. */
  serviceAuthorized?: boolean;
  /** Absolute corrected values for this Second Review only; charge is explicitly reviewed, never inferred. */
  correction?: { units: number; modifiers: string[]; charge: number };
};

/** Starting Second Review language for one service line, matched from the EOR. */
export type SecondReviewLineSuggestion = {
  lineItemId: string;
  /** Procedure code with modifiers. */
  code: string;
  reasonCodes: string[];
  /** Language to review, or null when the EOR does not identify a clear reason. */
  reason: string | null;
  source: "saved" | "system_template" | "none";
  template: { id: string; title: string; citation: string } | null;
  explanation: string | null;
};

/** A related DWC Independent Bill Review determination. */
export type SecondReviewIbrDecision = {
  /** DWC IBR case number. */
  id: string;
  date: string;
  code: string;
  issue: string;
  citation: string;
  outcome: string;
  awardedAmount: number | null;
  sourceUrl: string | null;
  matchedOn: string[];
  /** A favorable, same-code decision MindBill would cite automatically. */
  recommended: boolean;
  lineItemIds: string[];
};

export type SecondReviewSuggestions = {
  billId: string;
  lines: SecondReviewLineSuggestion[];
  ibr: {
    criteria: { codes: string[]; administrator: string | null; issues: string[] };
    decisions: SecondReviewIbrDecision[];
  };
};

export type SubmitSecondReviewInput = BillActorInput & {
  /** @deprecated Prefer `lineItems[].reason`; retained for older hosts. */
  reason?: string;
  /** @deprecated MindBill derives this from the payer response on the server. */
  payerClaimControlNumber?: string;
  disputedAmount?: number;
  attachmentIds: string[];
  route: BillSubmissionRoute;
  /** New PDFs to include with the Second Review. */
  documents?: BrowserBillSubmissionDocument[];
  destination?: SubmitBillInput["destination"];
  attention?: string;
  subject?: string;
  note?: string;
  cc?: string[];
  /** Line-by-line SBR-1 selections, reasons, and authorization answers. */
  lineItems?: SecondReviewLineInput[];
  /** IBR decisions cited in the reasons. MindBill attaches each original DWC
   *  determination PDF and encloses it in this Second Review packet. */
  ibrDecisionIds?: string[];
  /** @deprecated Prefer `lineItems`; omitted = the whole bill. */
  lineItemIds?: string[];
  /** @deprecated Prefer `lineItems[].serviceAuthorized`. */
  serviceAuthorized?: boolean;
};

export type ReportBillStatusId =
  | "message_left"
  | "eor_pending"
  | "eor_sent"
  | "bill_not_on_file"
  | "forwarded";

export type ReportBillStatusOption = {
  id: ReportBillStatusId;
  label: string;
  description: string;
};

/** The five supported outcomes of a bill payment-status phone call. */
export const REPORT_BILL_STATUS_OPTIONS: ReportBillStatusOption[] = [
  {
    id: "message_left",
    label: "Message Left Requesting Bill Payment Status",
    description: "Claims Administrator/Bill Review representative unavailable.",
  },
  {
    id: "eor_pending",
    label: "Explanation of Review (EOR) Pending",
    description: "Claims Administrator/Bill Review reported the bill is still in process and an EOR has not been generated or sent.",
  },
  {
    id: "eor_sent",
    label: "Explanation of Review (EOR) Sent",
    description: "Claims Administrator/Bill Review reported sending an EOR to the provider.",
  },
  {
    id: "bill_not_on_file",
    label: "Bill Not On File",
    description: "Claims Administrator/Bill Review reported not receiving the bill.",
  },
  {
    id: "forwarded",
    label: "Bill Forwarded to Different Payer / Network",
    description: "Claims Administrator/Bill Review reported forwarding the bill to a different payer.",
  },
];

/**
 * Outcome of a phone call to the Claims Administrator / Bill Review vendor
 * about an outstanding bill, recorded through the lifecycle actions endpoint.
 */
export type ReportBillStatusInput = BillActorInput & {
  status: ReportBillStatusId;
  company?: string;
  representativeName?: string;
  representativeRole?: string;
  phone?: string;
  callReference?: string;
  note?: string;
};

/**
 * Payload for the "report_bill_status" lifecycle action: records the outcome
 * of a phone call to the Claims Administrator / Bill Review vendor about an
 * outstanding bill. Posted through the generic lifecycle actions endpoint.
 */
export type ReportBillStatusActionInput = ReportBillStatusInput & {
  action: "report_bill_status";
};

/**
 * Payload for the "send_duplicate" lifecycle action. A duplicate is a fresh,
 * editable bill snapshot linked to the submitted original. The original stays
 * immutable; the biller must review the snapshot and explicitly confirm its
 * delivery route before this action is posted.
 */
export type SendDuplicateBillInput = ResubmitBillInput;

export type ReportBillStatusContacts = {
  claimsAdmin: {
    name: string;
    hoursOfOperation?: string;
    phones: Array<{ label: string; value: string }>;
  } | null;
  billReview: { name: string; phone?: string } | null;
};

/**
 * Partner-safe "who to call" summary for the Report Bill Status flow, derived
 * from the lifecycle delivery block (payer contacts + optional directory).
 */
export function reportBillStatusContacts(
  delivery: BillLifecycleDelivery,
): ReportBillStatusContacts {
  const payerName = delivery.payerName?.trim();
  const directory = delivery.directory ?? null;
  const phones: Array<{ label: string; value: string }> = [];
  const adjusterPhone = delivery.contacts.adjusterPhone?.trim();
  if (adjusterPhone) {
    const adjusterName = delivery.contacts.adjusterName?.trim();
    phones.push({
      label: adjusterName ? `Adjuster (${adjusterName})` : "Adjuster",
      value: adjusterPhone,
    });
  }
  const hours = directory?.hours?.trim();
  const claimsAdmin = payerName
    ? {
        name: payerName,
        ...(hours ? { hoursOfOperation: hours } : {}),
        phones,
      }
    : null;
  const billReviewContact = (directory?.billReview ?? []).find(
    (contact) => contact.name?.trim() || contact.phone?.trim(),
  );
  const billReview = billReviewContact
    ? {
        name: billReviewContact.name?.trim() || "Bill Review",
        ...(billReviewContact.phone?.trim() ? { phone: billReviewContact.phone.trim() } : {}),
      }
    : null;
  return { claimsAdmin, billReview };
}

/**
 * Default Second Bill Review dispute reason for an invalid med-legal denial.
 * Prefilled (editable) in the SBR panel.
 */
export const SECOND_REVIEW_REASON_TEMPLATE =
  "This is an appeal of the invalid denial of this med-legal bill; per LC §4622 and the OMFS/MLFS, payment is due as billed.";

/** Days allowed to request Second Review after service of the denial EOR. */
export const SECOND_REVIEW_WINDOW_DAYS = 90;

export type SecondReviewDeadline = {
  /** ISO date (yyyy-mm-dd) of the latest denial/EOR document. */
  eorDate: string;
  /** ISO date (yyyy-mm-dd) 90 days after the EOR date. */
  deadline: string;
};

/**
 * The 90-day Second Review window anchored on the latest EOR date, or null
 * when the lifecycle data provides no parseable denial/EOR date.
 */
export function secondReviewDeadline(
  eors: Array<Pick<BillEorDocument, "addedAt">>,
): SecondReviewDeadline | null {
  const times = eors
    .map((eor) => Date.parse(eor.addedAt))
    .filter((time) => Number.isFinite(time));
  if (!times.length) return null;
  const eorTime = Math.max(...times);
  const isoDate = (time: number): string => new Date(time).toISOString().slice(0, 10);
  return {
    eorDate: isoDate(eorTime),
    deadline: isoDate(eorTime + SECOND_REVIEW_WINDOW_DAYS * 86_400_000),
  };
}
/**
 * A corrected immutable snapshot submitted as the next attempt on the same bill.
 * The logical bill ID remains stable; only the submission/control-number attempt advances.
 */
export type ResubmitBillInput = BillActorInput & {
  reason?: string;
  bill: BrowserBillCreateInput;
  /** Explicit delivery route confirmed by the biller for this new attempt. */
  submission?: Omit<SubmitBillInput, keyof BillActorInput>;
  documents?: BrowserBillSubmissionDocument[];
};
/**
 * "Submit New Bill" from a CLOSED bill: the same snapshot payload shape as a
 * resubmission, but the server creates a FRESH original bill linked to the closed
 * predecessor — the closed bill stays closed and keeps its record, and the
 * submissions ribbon/timeline chains both records.
 */
export type SubmitNewBillInput = ResubmitBillInput;
export type SandboxSimulationScenario = "accepted" | "rejected" | "processed" | "denied" | "partial_payment" | "paid";
export type SimulateSandboxBillInput = {
  scenario: SandboxSimulationScenario;
  amount?: number;
  reasonCode?: string;
};

export type BrowserBillAddress = {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
};

/** Complete bill snapshot accepted by the browser create endpoint. */
export type BrowserBillCreateInput = {
  claimForm?: ClaimForm;
  formData?: BillFormData;
  externalId?: string;
  billingMode?: "med_legal" | "professional";
  patient: {
    id?: string;
    externalId?: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    dateOfBirth: string;
    ssn?: string;
    gender?: "M" | "F" | "X";
    phone?: string;
    address: BrowserBillAddress;
  };
  claim: {
    id?: string;
    externalId?: string;
    claimNumber: string;
    adjNumber?: string;
    medicalProviderNetworkId?: string | null;
    employer: string;
    dateOfInjury: string;
    injuryState?: string;
    description?: string;
    claimsAdministrator: {
      id: string;
      name: string;
      /** The chosen payer (subpayor) when the claims administrator requires payer selection. */
      payerId?: string;
    };
  };
  service: { date: string; endDate?: string | null; authorizationNumber?: string | null };
  billingProvider: { id?: string; name: string; taxId: string; taxIdType?: "EIN" | "SSN"; npi: string; phone: string; address: BrowserBillAddress } | { savedProviderId: string } | { sourceBillId: string };
  renderingProvider: {
    name: string;
    specialty?: string;
    npi: string;
    taxonomy: string;
    licenseNumber?: string;
    licenseState?: string;
    isQme?: boolean;
    isAme?: boolean;
  };
  serviceLocation: { name?: string; address: BrowserBillAddress; placeOfServiceCode: string };
  diagnoses: string[];
  serviceLines: Array<{
    formData?: BillLineFormData;
    code: string;
    modifiers?: string[];
    units?: number;
    charge?: number;
    serviceDate?: string;
    serviceDateEnd?: string | null;
    diagnosisPointers?: number[];
    /** Calculation context revalidated by the server before bill creation. */
    feeContext?: BillFeeContext;
    drug?: BilledDrug;
    /** Existing authorized RFA item linked to this service line. */
    rfaItemId?: string;
  }>;
};

/** Canonical immutable PDF snapshot sent with a bill submission. */
export type BrowserBillSubmissionDocument = {
  externalId?: string;
  filename: string;
  description?: string;
  documentType: BillReviewDocumentType;
  /** Opaque PWK report-type code selected by the component (for example OZ:J4). */
  reportTypeCode?: string;
  contentBase64: string;
};

export type BrowserBillSubmissionInput = BillActorInput & {
  /** Usually omitted: MindBill stamps the customer from the server-issued session.
   * If supplied, it must match the session customer restriction. */
  customerExternalId?: string;
  bill: BrowserBillCreateInput;
  /** Submit a saved draft (createBillDraftsClient). The draft must still be at this revision and unsent. */
  draft?: { id: string; revision: number };
  submission?: {
    route?: BillSubmissionRoute;
    destination?: SubmitBillInput["destination"];
    attention?: string;
    subject?: string;
    note?: string;
    cc?: string[];
  };
  documents?: BrowserBillSubmissionDocument[];
};

export type BrowserSubmittedBill = Record<string, unknown> & {
  id: string;
  customerExternalId?: string | null;
  externalId?: string | null;
  state?: string;
};

export type BrowserBillSubmissionResult = {
  billId: string;
  bill: BrowserSubmittedBill;
};

export type BillLifecycleClientOptions = {
  /** ID of an already-submitted bill. Bills are created only by the atomic server API. */
  billId: string;
  sessionEndpoint?: string | undefined;
  getSession?: BillLifecycleSessionProvider | undefined;
  apiBaseUrl?: string | undefined;
  fetch?: typeof globalThis.fetch | undefined;
};

/** Browser-session options for pre-submission reference-data lookups. */
export type BillReferenceClientOptions = Omit<BillLifecycleClientOptions, "billId">;

/** Browser-session options for an atomic immutable bill submission. */
export type BillSubmissionClientOptions = BillReferenceClientOptions;

export type BillSubmissionClient = {
  previewCms1500: (bill: BrowserBillSubmissionInput["bill"]) => Promise<Blob>;
  submitBill: (
    input: BrowserBillSubmissionInput,
    options?: { idempotencyKey?: string },
  ) => Promise<BrowserBillSubmissionResult>;
  clearSession: () => void;
};

/** Input for the pre-submission delivery preview keyed on a payer-directory id. */
export type BillDeliveryPreviewInput = {
  claimsAdministratorId: string;
  /** Selected payer child when the administrator exposes multiple payer routes. */
  payerId?: string;
  /** Two-letter state; defaults to CA server-side. */
  injuryState?: string;
};

export type BillProcedureCodeSearchInput = {
  query?: string;
  limit?: number;
  jurisdiction?: "CA" | "NY" | "OWCP";
};

/** Catalog membership does not imply that a fee is available for a service date. */
export type BillProcedureCodePage = {
  results: Array<{ code: string; description?: string }>;
  total: number;
  limit: number;
  jurisdiction: "CA" | "NY" | "OWCP";
  catalogAsOf: string | null;
};

export type BillFeeSource = {
  id: string;
  url: string;
  sha256?: string;
  effectiveFrom: string;
  effectiveThrough: string;
};

/** Drug identifiers and quantities printed on the professional claim. */
export type BilledDrug = {
  ndcNumber: string;
  metricQuantity: string;
  unitOfMeasure: "UN" | "ML" | "GR";
  administered?: {
    drugName: string;
    administeredAmount: string;
    doseUnit: "mg" | "mcg" | "g" | "mL" | "units";
    hcpcsCode: string;
    amountPerHcpcsUnit: string;
    amountPerNdcUnit: string;
    hcpcsUnitSource: string;
    productLabelSource: string;
    unitDefinitionsVerified: true;
  };
};

/** Minutes since midnight on the service date; end must follow start. */
export type CaAnesthesiaInterval = { startMinute: number; endMinute: number };
/** Opaque references to the actual physician and service record. */
export type CaAnesthesiaEvent = { physicianRef: string; recordRef: string; minute: number };
export type CaAnesthesiaParticipation = CaAnesthesiaInterval & { physicianRef: string; recordRef: string };
export type CaAnesthesiaOptionalParticipation = CaAnesthesiaParticipation | { applicability: "not_indicated"; recordRef: string };
export type CaMedicalDirectionCase = {
  caseRef: string;
  anesthetistRole: "crna" | "anesthesiologist_assistant" | "intern" | "resident" | "student_nurse_anesthetist";
  qualificationRecordRef: string;
  procedure: "cataract" | "iridectomy" | "other";
  interval: CaAnesthesiaInterval;
  activities: {
    preAnestheticEvaluation: CaAnesthesiaEvent;
    prescribedPlan: CaAnesthesiaEvent;
    demandingProcedures: CaAnesthesiaParticipation[];
    induction: CaAnesthesiaOptionalParticipation;
    emergence: CaAnesthesiaOptionalParticipation;
    qualifiedAnesthetistProcedures: CaAnesthesiaEvent;
    frequentMonitoring: { recordRef: string; observations: CaAnesthesiaEvent[] };
    indicatedPostAnesthesiaCare: CaAnesthesiaEvent;
  };
};
/** All overlapping cases across all payers, including cases outside this bill. */
export type CaMedicalDirection = {
  physicianRef: string;
  groupPhysicianRefs: string[];
  billedCaseRef: string;
  rosterScope: "all_overlapping_cases_all_payers";
  rosterRecordRef: string;
  cases: CaMedicalDirectionCase[];
  physicalPresenceAndImmediateAvailability: CaAnesthesiaParticipation[];
  otherPatientServices: Array<{
    kind: "emergency" | "labor_analgesia" | "obstetric_monitoring" | "receiving_patient" | "recovery_care" | "other";
    interval: CaAnesthesiaInterval;
    recordRef: string;
  }>;
};
export type CaMonitoredAnesthesiaCare = {
  medicallyNecessary: true;
  intraoperativePhysiologicalMonitoring: true;
  preparedForGeneralAnesthesiaOrAdverseReaction: true;
  perioperativeAnesthesiaCare: true;
  underlyingProcedureProvider: "different_provider" | "same_provider";
  underlyingProcedure: "nerve_block_or_injection" | "other";
};
/** Clinical facts supplement AA or QK; monitored care also requires QS. */
export type CaAnesthesiaContext = {
  providerKind: "physician";
  actualMinutes: number;
  placeOfService: string;
  completeSameDayServices: true;
  otherSameDayServices: boolean;
  codingRequirementsSatisfied: true;
  monitoredCare?: CaMonitoredAnesthesiaCare;
} & ({ personallyPerformedAlone: true; medicalDirection?: never } | { personallyPerformedAlone: false; medicalDirection: CaMedicalDirection });

export type CaPadbContext = {
  providerKind: "physician";
  placeOfService: "11";
  productKind: "injectable";
  bundledOrPackaged: false;
  codingRequirementsSatisfied: true;
  completeSameDayServices: true;
  sameDayServices: Array<{ code: string; units: number }>;
};

export type CaTherapyContext = {
    providerKind?: "physical_therapist" | "other";
    personallyPerformed?: boolean;
    hospitalPatient?: boolean;
    incidentToPhysicianService?: boolean;
    assistantInvolved?: boolean;
    placeOfService?: string;
    /** Whether an initial evaluation already occurred in this care episode, including prior dates. Omit when unknown. */
    priorInitialEvaluationInEpisode?: boolean;
    /** Required for timed treatment; initial evaluations are untimed. */
    directOneOnOneMinutes?: number;
    totalVisitMinutes?: number;
    visitsOnDate?: number;
    completeSameDayServices?: boolean;
    otherSameDayServices?: boolean;
    globalPeriodApplies?: boolean;
    hpsaBonusEligible?: boolean;
  };

/** Actual technical imaging facts. All fields are required when this context is supplied. */
export type CaTechnicalComponentContext = {
  /** Whether the billing provider or group furnished the service; purchased or outsourced services require review. */
  performedByBillingProviderGroup: boolean;
  patientHospitalStatus: "not_hospital_patient" | "hospital_inpatient_or_outpatient";
  supervisionLevel: "general" | "direct" | "personal";
  /** Actual session label, 1–64 letters/digits/._:-, beginning with a letter or digit. Never a patient identifier. */
  imagingSessionReference: string;
  /** All imaging for this patient/provider group/date is included, including separately billed services. */
  completeSameDayImagingServices: boolean;
};

/** Documented interpretation and imaging-session facts; an omitted completeness fact remains unknown. */
export type CaProfessionalComponentContext = {
  interpretationLocation: "same_as_patient_service" | "different_from_patient_service";
  supervisionLevel?: "general" | "direct" | "personal";
  /** Actual session label, 1–64 letters/digits/._:-, beginning with a letter or digit. Never a patient identifier. */
  imagingSessionReference?: string;
  /** All imaging for this patient/provider group/date is included, including separately billed services. */
  completeSameDayImagingServices?: boolean;
};

export type BillFeeQuoteInput = {
  drug?: BilledDrug;
  padbContext?: CaPadbContext;
  anesthesiaContext?: CaAnesthesiaContext;
  billingProviderId?: string;
  payerId?: string;
  code: string;
  dateOfService: string;
  chargeCents?: number;
  units?: number;
  modifiers?: string[];
  pages?: number;
  reportKind?: "progress" | "permanent_stationary_pr3" | "permanent_stationary_pr4";
  hasFeeAgreement?: boolean;
  serviceZip?: string;
  /** Explicit physical interpretation location for a professional component. */
  professionalComponentContext?: CaProfessionalComponentContext;
  technicalComponentContext?: CaTechnicalComponentContext;
  /** California equipment pricing uses worker residence, independently of service location. */
  dmeposContext?: {
    residenceZip: string;
    /** Continuous rental month for this equipment item; only for applicable RR lines. */
    rentalMonth?: number;
    /** Actual prior payments for this item, including rentals; explicit zero means no prior payments. */
    priorPaymentsCents?: number;
  };
  /** Supply verified service facts; omitted context can require review. */
  physicianContext?: {
    providerKind: "physician" | "physician_assistant" | "nurse_practitioner" | "clinical_nurse_specialist" | "other";
    incidentToPhysicianService?: boolean;
    placeOfService: string;
    standaloneService: boolean;
    globalPeriodApplies: boolean;
    hpsaBonusEligible: boolean;
  };
  therapyContext?: CaTherapyContext;
  catalogContext?: {
    codingRequirementsSatisfied: boolean;
    statusIAlternative?: "none" | "cpt" | "drug";
    completeSurgicalPackage?: boolean;
  };
  prolongedServiceContext?: {
    totalMinutes: number;
    relatedEvaluationDate: string;
    ongoingPatientManagement: boolean;
    personallyPerformed: boolean;
    timeCountedInOtherServices: boolean;
    completeSameDayServices: boolean;
    sameDayServices: Array<{ code: string; units: number }>;
  };
  reportQualification?:
    | { kind: "psychiatric_report"; requestedBy: "wcab" | "administrative_director"; medicalLegalEvaluation: boolean; reportPayableUnderMedicalLegalSchedule: boolean }
    | { kind: "consultation_report"; requestedBy: "wcab" | "administrative_director" | "qme" | "ame"; medicalLegalEvaluation: boolean; reportPayableUnderMedicalLegalSchedule: boolean }
    | { kind: "chart_notes"; requestedBy: "claims_administrator"; writtenRequest: boolean }
    | { kind: "duplicate_report"; requestedBy: "claims_administrator"; writtenRequest: boolean; relatedToBilling: boolean; initialRequiredCopy: boolean };
};

/** Authoritative service, provider, payer and location fields come from the submitted bill. */
export type BillFeeContext = Omit<BillFeeQuoteInput, "code" | "dateOfService" | "units" | "modifiers" | "chargeCents" | "billingProviderId" | "payerId" | "serviceZip" | "drug">;

/** Fee families returned by the California and practice-contract quote endpoint. */
export const BILL_FEE_QUOTE_BASES = [
  "ca_report", "ca_physician_rbrvs", "ca_therapy_rbrvs", "ca_clfs",
  "ca_simple_dispensed_drug", "ca_dmepos", "ca_padb", "ca_anesthesia", "payer_contract",
] as const;
export type BillFeeQuoteBasis = typeof BILL_FEE_QUOTE_BASES[number];

export type BillFeeQuote =
  | {
    status: "priced";
    /** Total for the service line in cents, already accounting for units. */
    amountCents: number;
    scheduleMaximumCents: number;
    /** Practice or entered billed charge, distinct from estimated reimbursement. */
    chargeAmountCents?: number;
    /**
     * The practice's saved charge for this code (its charge schedule × units), when the organization has one.
     * Independent of any charge entered on the bill; editors prefill an empty billed charge with it.
     */
    practiceChargeCents?: number;
    /** California statutory maximum when the quoted basis is a payer contract. */
    statutoryMaximumCents?: number;
    basis: BillFeeQuoteBasis;
    anesthesia?: { actualMinutes: number; baseUnits: number; timeUnitsTenths: number; conversionFactorCents: number; locality: string; medicalDirection?: { concurrentCases: number; baseReductionPercent: number; physicianPaymentPercent: 50 } };
    /** Relative-value calculation behind an RBRVS amount, for display. */
    calculation?: CaFeeCalculation;
    feeBreakdown?: { method: string; inputs: Array<{ label: string; value: string }>; steps: Array<{ label: string; value: string }> };
    provenance: BillFeeSource[];
    notes: string[];
    /** Conditions that do not change the amount, such as modifier documentation or visit caps. */
    findings?: BillFeeQuoteFinding[];
  }
  | { status: "requires_review" | "not_separately_payable"; reason: string; provenance: BillFeeSource[];
    /** Plain-language explanation for the biller, when available. */
    message?: string; findings?: BillFeeQuoteFinding[] };

export type BillFeeQuoteFinding = { code: string; message: string; citationUrl?: string };

/** Public DWC directory record. Only Approved records are selectable. */
export type MedicalProviderNetwork = {
  id: string;
  name: string;
  applicantName: string;
  applicantType?: string;
  status: "Approved" | "Terminated" | "Withdrawn";
  approvalDate?: string | null;
  website?: string | null;
};

export type BillReferenceClient = {
  listMedicalProviderNetworks: () => Promise<MedicalProviderNetwork[]>;
  listClaimsAdministrators: (input?: BillReviewPayerListInput) => Promise<BillReviewPayerPage>;
  searchClaimsAdministrators: (query: string, claimNumber?: string) => Promise<BillReviewPayer[]>;
  getClaimsAdministratorDirectory: (id: string, injuryState?: string) => Promise<BillClaimsAdministratorDirectory>;
  searchDiagnosisCodes: (query: string, limit?: number, offset?: number) => Promise<BillDiagnosisCode[]>;
  searchProcedureCodes: (input?: BillProcedureCodeSearchInput) => Promise<BillProcedureCodePage>;
  quoteFee: (input: BillFeeQuoteInput) => Promise<BillFeeQuote>;
  quoteClaimFees: (input: CaClaimFeeQuoteInput) => Promise<CaClaimFeeQuoteResult>;
  /** Expected payment for one treatment visit, priced with the organization's saved provider, location and claim. */
  quoteEncounter: (input: EncounterQuoteInput) => Promise<EncounterQuoteResult>;
  lookupPostalCode: (postalCode: string) => Promise<BillPostalPlace | null>;
  /**
   * Delivery-route preview (recommended route + selectable options + payer
   * contacts) for a claims administrator BEFORE the bill exists — powers the
   * send-route picker shown at submit time.
   */
  getDeliveryPreview: (input: BillDeliveryPreviewInput) => Promise<BillDeliveryOptions>;
  clearSession: () => void;
};

export type BillLifecycleClient = {
  getBillId: () => string;
  getLifecycle: (signal?: AbortSignal) => Promise<BillLifecycleData>;
  listMedicalProviderNetworks: () => Promise<MedicalProviderNetwork[]>;
  listClaimsAdministrators: (input?: BillReviewPayerListInput) => Promise<BillReviewPayerPage>;
  searchClaimsAdministrators: (query: string, claimNumber?: string) => Promise<BillReviewPayer[]>;
  getClaimsAdministratorDirectory: (id: string, injuryState?: string) => Promise<BillClaimsAdministratorDirectory>;
  searchDiagnosisCodes: (query: string, limit?: number, offset?: number) => Promise<BillDiagnosisCode[]>;
  searchProcedureCodes: (input?: BillProcedureCodeSearchInput) => Promise<BillProcedureCodePage>;
  quoteFee: (input: BillFeeQuoteInput) => Promise<BillFeeQuote>;
  quoteClaimFees: (input: CaClaimFeeQuoteInput) => Promise<CaClaimFeeQuoteResult>;
  quoteEncounter: (input: EncounterQuoteInput) => Promise<EncounterQuoteResult>;
  lookupPostalCode: (postalCode: string) => Promise<BillPostalPlace | null>;
  getDeliveryOptions: () => Promise<BillDeliveryOptions>;
  getDeliveryPreview: (input: BillDeliveryPreviewInput) => Promise<BillDeliveryOptions>;
  getAttachment: (attachmentId: string) => Promise<Blob>;
  getEor: (documentId: string) => Promise<Blob>;
  getElectronicEorPdf: () => Promise<Blob>;
  getPacket: () => Promise<Blob>;
  getSubmissionArtifact: (attemptId: string, artifactId: string) => Promise<Blob>;
  /** Prepare a self-filing packet. Does not file IBR or send anything to a payer. */
  prepareIbrPacket: () => Promise<Blob>;
  previewCourtesyCopy: (input: BillCourtesyCopyInput) => Promise<BillCourtesyCopyPreview>;
  /** Reuse the same key when retrying the same send after a network interruption. */
  sendCourtesyCopy: (input: BillCourtesyCopyInput & { packetHash: string }, idempotencyKey: string) => Promise<BillCourtesyCopyResult>;
  addNote: (input: AddBillNoteInput) => Promise<BillLifecycleData>;
  closeBill: (input: CloseBillInput) => Promise<BillLifecycleData>;
  reopenBill: (input: ReopenBillInput) => Promise<BillLifecycleData>;
  postPayment: (input: PostBillPaymentInput) => Promise<BillLifecycleData>;
  /** Rule-based starting reasons and related IBR decisions for the selected lines. Read-only. */
  getSecondReviewSuggestions: (lineItemIds?: string[], signal?: AbortSignal) => Promise<SecondReviewSuggestions>;
  submitSecondReview: (input: SubmitSecondReviewInput) => Promise<BillLifecycleData>;
  resubmitBill: (input: ResubmitBillInput) => Promise<BillLifecycleData>;
  submitNewBill: (input: SubmitNewBillInput) => Promise<BillLifecycleData>;
  sendDuplicateBill: (input: SendDuplicateBillInput) => Promise<BillLifecycleData>;
  reportBillStatus: (input: ReportBillStatusInput) => Promise<BillLifecycleData>;
  simulateSandbox: (input: SimulateSandboxBillInput) => Promise<BillLifecycleData>;
  clearSession: () => void;
};

const IBR_CITATION_HEADING = "Supporting Independent Bill Review determinations:";

/** One citation line, worded exactly as MindBill's own Second Review cites a decision. */
export function secondReviewIbrCitationLine(decision: Pick<SecondReviewIbrDecision, "id" | "outcome" | "issue" | "citation" | "sourceUrl">): string {
  return `• IBR ${decision.id} (${decision.outcome || "Unknown"}): ${decision.issue}${decision.citation ? ` — ${decision.citation}` : ""}${decision.sourceUrl ? ` — ${decision.sourceUrl}` : ""}`;
}

/** Append citations for decisions not already cited in the reason. */
export function appendSecondReviewIbrCitations(reason: string, decisions: Array<Pick<SecondReviewIbrDecision, "id" | "outcome" | "issue" | "citation" | "sourceUrl">>): string {
  const missing = decisions.filter((decision) => !reason.includes(`IBR ${decision.id} (`));
  if (!missing.length) return reason;
  const heading = reason.includes(IBR_CITATION_HEADING) ? "" : `${IBR_CITATION_HEADING}\n`;
  return `${reason.trim()}\n\n${heading}${missing.map(secondReviewIbrCitationLine).join("\n")}`.trim();
}

/** Remove one decision's citation line, and the heading when no citation remains. */
export function removeSecondReviewIbrCitation(reason: string, decisionId: string): string {
  const remaining = reason.split("\n").filter((line) => !line.startsWith(`• IBR ${decisionId} (`));
  if (!remaining.some((line) => line.startsWith("• IBR "))) return remaining.filter((line) => line !== IBR_CITATION_HEADING).join("\n").trim();
  return remaining.join("\n").trim();
}

const suggestionText = (value: unknown): string => typeof value === "string" ? value : "";
const suggestionTexts = (value: unknown): string[] => Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];

/** Validate the Second Review suggestion payload; unknown or malformed entries are dropped. */
export function normalizeSecondReviewSuggestions(value: unknown): SecondReviewSuggestions {
  const data = value && typeof value === "object" ? value as Record<string, unknown> : {};
  const ibr = data.ibr && typeof data.ibr === "object" ? data.ibr as Record<string, unknown> : {};
  const criteria = ibr.criteria && typeof ibr.criteria === "object" ? ibr.criteria as Record<string, unknown> : {};
  const sources = new Set(["saved", "system_template", "none"]);
  const lines = (Array.isArray(data.lines) ? data.lines : []).flatMap((entry): SecondReviewLineSuggestion[] => {
    if (!entry || typeof entry !== "object") return [];
    const line = entry as Record<string, unknown>;
    if (typeof line.lineItemId !== "string" || !line.lineItemId) return [];
    const template = line.template && typeof line.template === "object" ? line.template as Record<string, unknown> : null;
    const reason = typeof line.reason === "string" && line.reason.trim() ? line.reason : null;
    return [{
      lineItemId: line.lineItemId,
      code: suggestionText(line.code),
      reasonCodes: suggestionTexts(line.reasonCodes),
      reason,
      source: reason && typeof line.source === "string" && sources.has(line.source) ? line.source as SecondReviewLineSuggestion["source"] : "none",
      template: template && typeof template.id === "string" ? { id: template.id, title: suggestionText(template.title), citation: suggestionText(template.citation) } : null,
      explanation: typeof line.explanation === "string" ? line.explanation : null,
    }];
  });
  const decisions = (Array.isArray(ibr.decisions) ? ibr.decisions : []).flatMap((entry): SecondReviewIbrDecision[] => {
    if (!entry || typeof entry !== "object") return [];
    const decision = entry as Record<string, unknown>;
    if (typeof decision.id !== "string" || !decision.id) return [];
    const sourceUrl = typeof decision.sourceUrl === "string" && /^https:\/\//.test(decision.sourceUrl) ? decision.sourceUrl : null;
    return [{
      id: decision.id,
      date: suggestionText(decision.date),
      code: suggestionText(decision.code),
      issue: suggestionText(decision.issue),
      citation: suggestionText(decision.citation),
      outcome: suggestionText(decision.outcome),
      awardedAmount: typeof decision.awardedAmount === "number" && Number.isFinite(decision.awardedAmount) ? decision.awardedAmount : null,
      sourceUrl,
      matchedOn: suggestionTexts(decision.matchedOn),
      recommended: decision.recommended === true,
      lineItemIds: suggestionTexts(decision.lineItemIds),
    }];
  });
  return {
    billId: suggestionText(data.billId),
    lines,
    ibr: {
      criteria: {
        codes: suggestionTexts(criteria.codes),
        administrator: typeof criteria.administrator === "string" ? criteria.administrator : null,
        issues: suggestionTexts(criteria.issues),
      },
      decisions,
    },
  };
}

async function responseError(response: Response, fallback: string): Promise<Error> {
  const body: unknown = await response.json().catch(() => null);
  const detail = body && typeof body === "object"
    ? (body as { detail?: unknown; message?: unknown; error?: unknown }).detail
      ?? (body as { message?: unknown }).message
      ?? (body as { error?: unknown }).error
    : null;
  return new Error(typeof detail === "string" ? detail : fallback);
}

function isSessionFresh(session: BillLifecycleSession | null): boolean {
  if (!session) return false;
  if (!session.expiresAt) return true;
  const expiresAt = new Date(session.expiresAt).getTime();
  return Number.isFinite(expiresAt) && expiresAt > Date.now() + 30_000;
}

function normalizeSession(value: unknown): BillLifecycleSession {
  if (!value || typeof value !== "object") {
    throw new Error("The billing session endpoint returned an invalid response.");
  }
  const candidate = value as { token?: unknown; expiresAt?: unknown; apiBaseUrl?: unknown; session?: unknown; data?: unknown };
  const nested = candidate.session ?? candidate.data;
  const session = nested && typeof nested === "object" ? nested as typeof candidate : candidate;
  if (typeof session.token !== "string" || session.token.length < 8) {
    throw new Error("The billing session endpoint did not return a browser session token.");
  }
  return {
    token: session.token,
    ...(typeof session.expiresAt === "string" ? { expiresAt: session.expiresAt } : {}),
    ...(typeof session.apiBaseUrl === "string" ? { apiBaseUrl: session.apiBaseUrl } : {}),
  };
}

function normalizeLifecycle(value: unknown): BillLifecycleData {
  if (!value || typeof value !== "object") {
    throw new Error("The billing service returned an invalid bill lifecycle.");
  }
  const data = value as Partial<BillLifecycleData>;
  if (
    !["sandbox", "live"].includes(data.environment ?? "")
    || !data.bill
    || !data.patient
    || !data.injury
    || !data.lifecycle
    || !Array.isArray(data.lifecycle.actions)
    || !Array.isArray(data.eors)
    || !Array.isArray(data.activity)
    || !Array.isArray(data.payments)
    || !data.remittance
    || typeof data.remittance !== "object"
    || !data.delivery
    || typeof data.delivery !== "object"
    || !data.delivery.contacts
    || typeof data.delivery.contacts !== "object"
    || (data.rejection != null && (
      typeof data.rejection !== "object"
      || typeof data.rejection.reason !== "string"
      || (data.rejection.issues != null && (
        !Array.isArray(data.rejection.issues)
        || data.rejection.issues.some((issue) => (
          !issue
          || typeof issue !== "object"
          || typeof issue.description !== "string"
          || (issue.code != null && typeof issue.code !== "string")
        ))
      ))
    ))
  ) {
    throw new Error("The billing service returned an invalid bill lifecycle.");
  }
  return data as BillLifecycleData;
}

function normalizeDeliveryOptions(value: unknown): BillDeliveryOptions {
  if (!value || typeof value !== "object") {
    throw new Error("The billing service returned invalid delivery options.");
  }
  const data = value as Partial<BillDeliveryOptions>;
  if (
    typeof data.payerName !== "string"
    || !data.recommended
    || typeof data.recommended !== "object"
    || !["ebill", "fax", "mail", "email"].includes(data.recommended.route ?? "")
    || !Array.isArray(data.options)
    || !data.contacts
  ) {
    throw new Error("The billing service returned invalid delivery options.");
  }
  return data as BillDeliveryOptions;
}

function idempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") return globalThis.crypto.randomUUID();
  return `mb-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Browser-safe client for one atomic create-and-submit operation.
 *
 * The browser session is short-lived and origin-bound. The host application
 * never needs the Partner API document schema or a long-lived API key.
 */
export function createBillSubmissionClient({
  sessionEndpoint = DEFAULT_SESSION_ENDPOINT,
  getSession,
  apiBaseUrl = DEFAULT_API_BASE_URL,
  fetch: fetchOverride,
}: BillSubmissionClientOptions = {}): BillSubmissionClient {
  const fetcher = fetchOverride ?? globalThis.fetch;
  if (typeof fetcher !== "function") throw new Error("A Fetch API implementation is required.");
  let session: BillLifecycleSession | null = null;
  let sessionRequest: Promise<BillLifecycleSession> | null = null;

  const mintSession = async (signal: AbortSignal, force = false): Promise<BillLifecycleSession> => {
    if (!force && isSessionFresh(session)) return session as BillLifecycleSession;
    if (!force && sessionRequest) return sessionRequest;
    const pending = getSession
      ? getSession({ signal })
      : fetcher(sessionEndpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({}),
        signal,
      }).then(async (response) => {
        if (!response.ok) throw await responseError(response, "The billing session could not be created.");
        return response.json();
      });
    const request = Promise.resolve(pending).then(normalizeSession).then((nextSession) => {
      session = nextSession;
      return nextSession;
    }).finally(() => {
      if (sessionRequest === request) sessionRequest = null;
    });
    sessionRequest = request;
    return request;
  };

  const submitBill = async (
    input: BrowserBillSubmissionInput,
    options: { idempotencyKey?: string } = {},
  ): Promise<BrowserBillSubmissionResult> => {
    const controller = new AbortController();
    const requestIdempotencyKey = options.idempotencyKey ?? idempotencyKey();
    let browserSession = await mintSession(controller.signal);
    const perform = (current: BillLifecycleSession) => {
      const base = (current.apiBaseUrl ?? apiBaseUrl).replace(/\/$/, "");
      return fetcher(`${base}/partner/v2/bills`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${current.token}`,
          "content-type": "application/json",
          "idempotency-key": requestIdempotencyKey,
        },
        body: JSON.stringify(input),
        signal: controller.signal,
      });
    };
    let response = await perform(browserSession);
    if (response.status === 401) {
      session = null;
      browserSession = await mintSession(controller.signal, true);
      response = await perform(browserSession);
    }
    if (!response.ok) throw await responseError(response, "The bill could not be submitted.");
    const body: unknown = await response.json().catch(() => null);
    const envelope = body && typeof body === "object" && "data" in body
      ? (body as { data?: unknown }).data
      : body;
    if (!envelope || typeof envelope !== "object" || typeof (envelope as { id?: unknown }).id !== "string") {
      throw new Error("The billing service submitted the bill but returned an invalid bill ID.");
    }
    const bill = envelope as BrowserSubmittedBill;
    return { billId: bill.id, bill };
  };

  const previewCms1500 = async (bill: BrowserBillSubmissionInput["bill"]): Promise<Blob> => {
    const controller = new AbortController();
    let browserSession = await mintSession(controller.signal);
    const perform = (current: BillLifecycleSession) => {
      const base = (current.apiBaseUrl ?? apiBaseUrl).replace(/\/$/, "");
      return fetcher(`${base}/partner/v2/bills/preview-cms1500`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${current.token}`,
          "content-type": "application/json",
        },
        body: JSON.stringify(bill),
        signal: controller.signal,
      });
    };
    let response = await perform(browserSession);
    if (response.status === 401) {
      session = null;
      browserSession = await mintSession(controller.signal, true);
      response = await perform(browserSession);
    }
    if (!response.ok) throw await responseError(response, "The CMS-1500 preview could not be generated.");
    if (!response.headers.get("content-type")?.includes("application/pdf")) {
      throw new Error("The billing service returned an invalid CMS-1500 preview.");
    }
    return response.blob();
  };

  return {
    previewCms1500,
    submitBill,
    clearSession() { session = null; sessionRequest = null; },
  };
}

/** Browser-safe, framework-neutral client for an already-submitted bill. */
export function createBillLifecycleClient({
  billId,
  sessionEndpoint = DEFAULT_SESSION_ENDPOINT,
  getSession,
  apiBaseUrl = DEFAULT_API_BASE_URL,
  fetch: fetchOverride,
}: BillLifecycleClientOptions): BillLifecycleClient {
  const fetcher = fetchOverride ?? globalThis.fetch;
  if (typeof fetcher !== "function") throw new Error("A Fetch API implementation is required.");
  let session: BillLifecycleSession | null = null;
  let sessionRequest: Promise<BillLifecycleSession> | null = null;
  const currentBillId = billId.trim();
  if (!currentBillId) throw new Error("billId is required for the submitted-bill lifecycle client.");

  const mintSession = async (signal: AbortSignal, force = false): Promise<BillLifecycleSession> => {
    if (!force && isSessionFresh(session)) return session as BillLifecycleSession;
    if (!force && sessionRequest) return sessionRequest;
    const pending = getSession
      ? getSession({ signal })
      : fetcher(sessionEndpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({}),
        signal,
      }).then(async (response) => {
        if (!response.ok) throw await responseError(response, "The billing session could not be created.");
        return response.json();
      });
    const request = Promise.resolve(pending).then(normalizeSession).then((nextSession) => {
      session = nextSession;
      return nextSession;
    }).finally(() => {
      if (sessionRequest === request) sessionRequest = null;
    });
    sessionRequest = request;
    return request;
  };

  const request = async (path: string, init: RequestInit = {}, providedSignal?: AbortSignal): Promise<Response> => {
    const controller = providedSignal ? null : new AbortController();
    const signal = providedSignal ?? controller?.signal;
    if (!signal) throw new Error("An AbortSignal could not be created.");
    let browserSession = await mintSession(signal);
    const perform = (current: BillLifecycleSession) => {
      const base = (current.apiBaseUrl ?? apiBaseUrl).replace(/\/$/, "");
      const headers = new Headers(init.headers);
      headers.set("authorization", `Bearer ${current.token}`);
      return fetcher(`${base}${path}`, { ...init, headers, signal });
    };
    let response = await perform(browserSession);
    if (response.status === 401) {
      session = null;
      browserSession = await mintSession(signal, true);
      response = await perform(browserSession);
    }
    return response;
  };

  const billPath = (suffix = ""): string =>
    `/partner/v2/bills/${encodeURIComponent(currentBillId)}${suffix}`;

  const loadLifecycle = async (signal?: AbortSignal) => {
    const response = await request(billPath("/lifecycle"), {}, signal);
    if (!response.ok) throw await responseError(response, "Bill lifecycle could not be loaded.");
    const body = await response.json() as { data?: unknown };
    return normalizeLifecycle(body.data);
  };

  const listMedicalProviderNetworks = async (): Promise<MedicalProviderNetwork[]> => {
    const response = await request("/partner/v2/medical-provider-networks");
    if (!response.ok) throw await responseError(response, "Medical provider networks could not be loaded.");
    const body = await response.json() as { data?: unknown };
    if (!Array.isArray(body.data)) throw new Error("Medical provider networks returned an invalid response.");
    return body.data.flatMap((value): MedicalProviderNetwork[] => {
      if (!value || typeof value !== "object") return [];
      const row = value as Record<string, unknown>;
      if (row.status !== "Approved" || typeof row.id !== "string" || typeof row.name !== "string" || typeof row.applicantName !== "string") return [];
      return [{ id: row.id, name: row.name, applicantName: row.applicantName, status: "Approved",
        ...(typeof row.applicantType === "string" ? { applicantType: row.applicantType } : {}),
        ...(typeof row.approvalDate === "string" ? { approvalDate: row.approvalDate } : {}),
        ...(typeof row.website === "string" ? { website: row.website } : {}),
      }];
    });
  };

  const listClaimsAdministrators = async (input: BillReviewPayerListInput = {}): Promise<BillReviewPayerPage> => {
    const params = new URLSearchParams();
    if (input.query?.trim()) params.set("q", input.query.trim());
    if (input.claimNumber?.trim()) params.set("claimNumber", input.claimNumber.trim());
    if (input.sourceClaimsAdministratorName?.trim()) params.set("sourceClaimsAdministratorName", input.sourceClaimsAdministratorName.trim());
    if (input.employerName?.trim()) params.set("employerName", input.employerName.trim());
    const limit = Math.max(1, Math.min(100, Math.floor(input.limit ?? 50)));
    const offset = Math.max(0, Math.floor(input.offset ?? 0));
    params.set("limit", String(limit));
    if (offset) params.set("offset", String(offset));
    const response = await request(`/partner/v2/claims-administrators?${params.toString()}`);
    if (!response.ok) throw await responseError(response, "Claims administrator search is unavailable.");
    const body = await response.json() as { results?: unknown; suggestions?: unknown; total?: unknown; nextOffset?: unknown; recommendedId?: unknown };
    if (!Array.isArray(body.results)) throw new Error("Claims administrator search returned an invalid response.");
    const normalizePayer = (value: unknown): BillReviewPayer | null => {
      if (!value || typeof value !== "object") return null;
      const payer = value as Record<string, unknown>;
      if (typeof payer.id !== "string" || typeof payer.name !== "string") return null;
      const payers = Array.isArray(payer.payers) ? payer.payers.flatMap((option): BillReviewPayerOption[] => {
        if (!option || typeof option !== "object") return [];
        const candidate = option as Record<string, unknown>;
        const id = typeof candidate.id === "string" ? candidate.id : typeof candidate.key === "string" ? candidate.key : null;
        if (!id || typeof candidate.label !== "string") return [];
        return [{
          id,
          label: candidate.label,
          ...(typeof candidate.default === "boolean" ? { default: candidate.default } : {}),
          ...(typeof candidate.active === "boolean" ? { active: candidate.active } : {}),
          ...(Array.isArray(candidate.aliases) ? { aliases: candidate.aliases.filter((item): item is string => typeof item === "string") } : {}),
          ...(typeof candidate.hint === "string" ? { hint: candidate.hint } : {}),
          ...(Array.isArray(candidate.affiliatedEntities) ? { affiliatedEntities: candidate.affiliatedEntities.filter((item): item is string => typeof item === "string") } : {}),
          ...(typeof candidate.route === "string" ? { route: candidate.route } : {}),
          ...(typeof candidate.optionType === "string" ? { optionType: candidate.optionType } : {}),
          ...(typeof candidate.deliveryType === "string" ? { deliveryType: candidate.deliveryType } : {}),
          ...(typeof candidate.clearinghouse === "string" ? { clearinghouse: candidate.clearinghouse } : {}),
          ...(typeof candidate.payerId === "string" ? { payerId: candidate.payerId } : {}),
          ...(typeof candidate.sourceClearinghouse === "string" ? { sourceClearinghouse: candidate.sourceClearinghouse } : {}),
          ...(typeof candidate.sourcePayerId === "string" ? { sourcePayerId: candidate.sourcePayerId } : {}),
          ...(candidate.clearinghousePayerIds && typeof candidate.clearinghousePayerIds === "object" && !Array.isArray(candidate.clearinghousePayerIds)
            ? { clearinghousePayerIds: Object.fromEntries(Object.entries(candidate.clearinghousePayerIds).filter((entry): entry is [string, string] => typeof entry[1] === "string")) }
            : {}),
          ...(typeof candidate.preferredClearinghouse === "string" ? { preferredClearinghouse: candidate.preferredClearinghouse } : {}),
        }];
      }) : undefined;
      const claimNumberPatterns = Array.isArray(payer.claimNumberPatterns) ? payer.claimNumberPatterns.flatMap((value): BillClaimsAdministratorPattern[] => {
        if (!value || typeof value !== "object") return [];
        const pattern = value as Record<string, unknown>;
        if (typeof pattern.pattern !== "string") return [];
        return [{
          pattern: pattern.pattern,
          ...(typeof pattern.length === "string" || typeof pattern.length === "number" ? { length: pattern.length } : {}),
          ...(typeof pattern.example === "string" ? { example: pattern.example } : {}),
          ...(typeof pattern.matches === "boolean" || pattern.matches === null ? { matches: pattern.matches as boolean | null } : {}),
        }];
      }) : undefined;
      return {
        id: payer.id,
        name: payer.name,
        ...(typeof payer.hasElectronic === "boolean" ? { hasElectronic: payer.hasElectronic } : {}),
        ...(Array.isArray(payer.states) ? { states: payer.states.filter((state): state is string => typeof state === "string") } : {}),
        ...(["high", "medium", "directory"].includes(String(payer.confidence)) ? { confidence: payer.confidence as NonNullable<BillReviewPayer["confidence"]> } : {}),
        ...(typeof payer.recommended === "boolean" ? { recommended: payer.recommended } : {}),
        ...(typeof payer.payerSelectionRequired === "boolean" ? { payerSelectionRequired: payer.payerSelectionRequired } : {}),
        ...(payers ? { payers } : {}),
        ...(Array.isArray(payer.aliases) ? { aliases: payer.aliases.filter((item): item is string => typeof item === "string") } : {}),
        ...(Array.isArray(payer.affiliatedEntities) ? { affiliatedEntities: payer.affiliatedEntities.filter((item): item is string => typeof item === "string") } : {}),
        ...(typeof payer.claimNumberHint === "string" ? { claimNumberHint: payer.claimNumberHint } : {}),
        ...(claimNumberPatterns ? { claimNumberPatterns } : {}),
        ...(typeof payer.route === "string" ? { route: payer.route } : {}),
        ...(Array.isArray(payer.signals) ? { signals: payer.signals.flatMap((signal) => {
          if (!signal || typeof signal !== "object") return [];
          const candidate = signal as Record<string, unknown>;
          if (!["name", "claim_number"].includes(String(candidate.kind)) || !["match", "warning"].includes(String(candidate.state)) || typeof candidate.label !== "string") return [];
          return [{ kind: candidate.kind as "name" | "claim_number", state: candidate.state as "match" | "warning", label: candidate.label }];
        }) } : {}),
      };
    };
    const results = body.results.flatMap((value): BillReviewPayer[] => {
      const payer = normalizePayer(value);
      return payer ? [payer] : [];
    });
    const suggestions = Array.isArray(body.suggestions) ? body.suggestions.flatMap((value): BillReviewPayerSuggestion[] => {
      const payer = normalizePayer(value);
      if (!payer || !value || typeof value !== "object") return [];
      const suggestion = value as Record<string, unknown>;
      if (typeof suggestion.deterministic !== "boolean" || typeof suggestion.reason !== "string") return [];
      return [{ ...payer, deterministic: suggestion.deterministic, reason: suggestion.reason, ...(typeof suggestion.selectedPayerId === "string" ? { selectedPayerId: suggestion.selectedPayerId } : {}) }];
    }) : [];
    const total = typeof body.total === "number" && Number.isFinite(body.total) ? Math.max(results.length, Math.floor(body.total)) : offset + results.length;
    return {
      results,
      ...(suggestions.length ? { suggestions } : {}),
      total,
      ...(typeof body.nextOffset === "number" && Number.isFinite(body.nextOffset) ? { nextOffset: Math.floor(body.nextOffset) } : {}),
      ...(typeof body.recommendedId === "string" ? { recommendedId: body.recommendedId } : {}),
    };
  };

  const searchClaimsAdministrators = async (query: string, claimNumber?: string): Promise<BillReviewPayer[]> =>
    (await listClaimsAdministrators({ query, ...(claimNumber ? { claimNumber } : {}) })).results;

  const getClaimsAdministratorDirectory = async (id: string, injuryState?: string): Promise<BillClaimsAdministratorDirectory> => {
    const params = new URLSearchParams();
    if (injuryState?.trim()) params.set("injuryState", injuryState.trim());
    const response = await request(`/partner/v2/claims-administrators/${encodeURIComponent(id)}${params.size ? `?${params}` : ""}`);
    if (!response.ok) throw await responseError(response, "Claims administrator details are unavailable.");
    const body = await response.json() as { data?: unknown };
    if (!body.data || typeof body.data !== "object") throw new Error("Claims administrator details returned an invalid response.");
    return body.data as BillClaimsAdministratorDirectory;
  };

  const searchDiagnosisCodes = async (query: string, limit = 30, offset = 0): Promise<BillDiagnosisCode[]> => {
    const params = new URLSearchParams({ q: query.trim(), limit: String(Math.max(1, Math.min(100, limit))) });
    if (offset > 0) params.set("offset", String(Math.max(0, Math.floor(offset))));
    const response = await request(`/partner/v2/diagnosis-codes?${params.toString()}`);
    if (!response.ok) throw await responseError(response, "Diagnosis-code search is unavailable.");
    const body = await response.json() as { results?: unknown };
    if (!Array.isArray(body.results)) throw new Error("Diagnosis-code search returned an invalid response.");
    return body.results.flatMap((value): BillDiagnosisCode[] => {
      if (!value || typeof value !== "object") return [];
      const candidate = value as { code?: unknown; description?: unknown };
      return typeof candidate.code === "string" && typeof candidate.description === "string"
        ? [{ code: candidate.code, description: candidate.description }]
        : [];
    });
  };

  const searchProcedureCodes = async (input: BillProcedureCodeSearchInput = {}): Promise<BillProcedureCodePage> => {
    const limit = Number.isFinite(input.limit) ? Math.max(1, Math.min(100, Math.floor(input.limit!))) : 30;
    const params = new URLSearchParams({ q: input.query?.trim() ?? "", limit: String(limit), jurisdiction: input.jurisdiction?.trim().toUpperCase() || "CA" });
    const response = await request(`/partner/v2/procedure-codes?${params}`);
    if (!response.ok) throw await responseError(response, "Procedure-code search is unavailable.");
    const body = await response.json() as Partial<BillProcedureCodePage>;
    if (!Array.isArray(body.results) || typeof body.total !== "number" || !Number.isFinite(body.total) || body.total < 0 || typeof body.limit !== "number" || !Number.isFinite(body.limit) || body.limit < 1 || !["CA", "NY", "OWCP"].includes(body.jurisdiction ?? "") || !(body.catalogAsOf === null || typeof body.catalogAsOf === "string")) {
      throw new Error("Procedure-code search returned an invalid response.");
    }
    return {
      results: body.results.flatMap((entry) => entry && typeof entry.code === "string" && entry.code.trim()
        ? [{ code: entry.code, ...(typeof entry.description === "string" && entry.description.trim() ? { description: entry.description.trim().slice(0, 200) } : {}) }] : []),
      total: body.total,
      limit: body.limit,
      jurisdiction: body.jurisdiction!,
      catalogAsOf: body.catalogAsOf,
    };
  };

  const quoteEncounter = async (input: EncounterQuoteInput): Promise<EncounterQuoteResult> => {
    const response = await request("/partner/v2/fee-quotes/encounter", {
      method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(input),
    });
    if (!response.ok) throw await responseError(response, "Encounter fee pricing is unavailable.");
    const body = await response.json() as { data?: unknown };
    return parseEncounterQuote(body.data, input);
  };

  const quoteClaimFees = async (input: CaClaimFeeQuoteInput): Promise<CaClaimFeeQuoteResult> => {
    const response = await request("/partner/v2/fee-quotes/ca/claim", {
      method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(input),
    });
    if (!response.ok) throw await responseError(response, "Claim fee pricing is unavailable.");
    const body = await response.json() as { data?: unknown };
    return parseCaClaimFeeQuote(body.data, input);
  };

  const quoteFee = async (input: BillFeeQuoteInput): Promise<BillFeeQuote> => {
    const response = await request("/partner/v2/fee-quotes", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!response.ok) throw await responseError(response, "Fee-schedule pricing is unavailable.");
    const body = await response.json() as { data?: BillFeeQuote };
    const quote = body.data;
    if (!quote || !Array.isArray(quote.provenance)) throw new Error("Fee quote returned an invalid response.");
    if (quote.status === "priced") {
      if (!Number.isSafeInteger(quote.amountCents) || quote.amountCents < 0 || !Number.isSafeInteger(quote.scheduleMaximumCents) || quote.scheduleMaximumCents < 0 || !BILL_FEE_QUOTE_BASES.includes(quote.basis) || !Array.isArray(quote.notes) || quote.notes.some((note) => typeof note !== "string")) throw new Error("Fee quote returned an invalid response.");
    } else if ((quote.status !== "requires_review" && quote.status !== "not_separately_payable") || typeof quote.reason !== "string") {
      throw new Error("Fee quote returned an invalid response.");
    }
    return quote;
  };

  const lookupPostalCode = async (postalCode: string): Promise<BillPostalPlace | null> => {
    const params = new URLSearchParams({ postalCode: postalCode.trim() });
    const response = await request(`/partner/v2/postal-codes?${params.toString()}`);
    if (response.status === 404) return null;
    if (!response.ok) throw await responseError(response, "ZIP lookup is unavailable.");
    const body = await response.json() as { city?: unknown; state?: unknown };
    if (typeof body.city !== "string" || typeof body.state !== "string") {
      throw new Error("ZIP lookup returned an invalid response.");
    }
    return { city: body.city, state: body.state };
  };

  const mutation = async (path: string, init: RequestInit, fallback: string) => {
    const headers = new Headers(init.headers);
    headers.set("idempotency-key", idempotencyKey());
    const response = await request(path, { ...init, headers });
    if (!response.ok) throw await responseError(response, fallback);
    return response;
  };
  const action = async (input: Record<string, unknown>, fallback: string): Promise<BillLifecycleData> => {
    const response = await mutation(billPath("/actions"), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    }, fallback);
    const body = await response.json() as { data?: unknown };
    return normalizeLifecycle(body.data);
  };
  const simulateSandbox = async (input: SimulateSandboxBillInput): Promise<BillLifecycleData> => {
    await mutation(`/partner/v2/sandbox/bills/${encodeURIComponent(currentBillId)}/simulate`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    }, "Sandbox lifecycle could not be simulated.");
    return loadLifecycle();
  };
  return {
    getBillId() { return currentBillId; },
    clearSession() { session = null; sessionRequest = null; },
    getLifecycle: loadLifecycle,
    listMedicalProviderNetworks,
    listClaimsAdministrators,
    searchClaimsAdministrators,
    getClaimsAdministratorDirectory,
    searchDiagnosisCodes,
    searchProcedureCodes,
    quoteFee,
    quoteClaimFees,
    quoteEncounter,
    lookupPostalCode,
    async getDeliveryOptions() {
      const response = await request(billPath("/delivery-options"));
      if (!response.ok) throw await responseError(response, "Delivery options could not be loaded.");
      const body = await response.json() as { data?: unknown };
      return normalizeDeliveryOptions(body.data ?? body);
    },
    async getDeliveryPreview(input) {
      const params = new URLSearchParams({ claimsAdministratorId: input.claimsAdministratorId.trim() });
      if (input.payerId?.trim()) params.set("payerId", input.payerId.trim());
      if (input.injuryState?.trim()) params.set("injuryState", input.injuryState.trim());
      const response = await request(`/partner/v2/delivery-preview?${params.toString()}`);
      if (!response.ok) throw await responseError(response, "Delivery routes could not be loaded.");
      const body = await response.json() as { data?: unknown };
      return normalizeDeliveryOptions(body.data ?? body);
    },
    async getAttachment(attachmentId) {
      const response = await request(billPath(`/documents/${encodeURIComponent(attachmentId)}`));
      if (!response.ok) throw await responseError(response, "Document could not be opened.");
      return response.blob();
    },
    async getEor(documentId) {
      const response = await request(billPath(`/eors/${encodeURIComponent(documentId)}`));
      if (!response.ok) throw await responseError(response, "EOR could not be opened.");
      return response.blob();
    },
    async getElectronicEorPdf() {
      const response = await request(billPath("/eor/pdf"));
      if (!response.ok) throw await responseError(response, "EOR PDF could not be downloaded.");
      return response.blob();
    },
    async getPacket() {
      const response = await request(billPath("/packet"));
      if (!response.ok) throw await responseError(response, "Submission packet could not be downloaded.");
      return response.blob();
    },
    async getSubmissionArtifact(attemptId, artifactId) {
      const response = await request(billPath(`/submissions/${encodeURIComponent(attemptId)}/artifacts/${encodeURIComponent(artifactId)}`));
      if (!response.ok) throw await responseError(response, "The retained submission file could not be downloaded.");
      return response.blob();
    },
    async getSecondReviewSuggestions(lineItemIds, signal) {
      const query = lineItemIds ? `?${new URLSearchParams({ lineItemIds: lineItemIds.join(",") }).toString()}` : "";
      const response = await request(billPath(`/second-review-suggestions${query}`), {}, signal);
      if (!response.ok) throw await responseError(response, "Second Review suggestions could not be loaded.");
      const body = await response.json() as { data?: unknown };
      return normalizeSecondReviewSuggestions(body.data);
    },
    async prepareIbrPacket() {
      const prepared = await mutation(billPath("/actions"), {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ action: "independent_bill_review" }),
      }, "IBR packet could not be prepared.");
      const body = await prepared.json() as { packetUrl?: unknown };
      // Never forward the browser bearer token to an arbitrary response URL.
      if (typeof body.packetUrl !== "string" || !/^\/partner\/v2\/(?:browser\/)?bills\/[A-Za-z0-9_-]+\/ibr-packet$/.test(body.packetUrl)) throw new Error("The billing service returned an invalid IBR packet path.");
      // The server may resolve a root bill to its current linked bill.
      const response = await request(body.packetUrl);
      if (!response.ok) throw await responseError(response, "IBR packet could not be downloaded.");
      return response.blob();
    },
    async previewCourtesyCopy(input) {
      const response = await request(billPath("/courtesy-forward"), {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ ...input, mode: "preview" }),
      });
      if (!response.ok) throw await responseError(response, "Courtesy copy could not be previewed.");
      return response.json() as Promise<BillCourtesyCopyPreview>;
    },
    async sendCourtesyCopy(input, key) {
      if (!key.trim() || key.length > 255) throw new Error("A stable idempotency key is required to send a courtesy copy.");
      const response = await request(billPath("/courtesy-forward"), {
        method: "POST", headers: { "content-type": "application/json", "idempotency-key": key },
        body: JSON.stringify({ ...input, mode: "send" }),
      });
      if (!response.ok) throw await responseError(response, "Courtesy copy could not be sent. Retry with the same key to check the result safely.");
      return response.json() as Promise<BillCourtesyCopyResult>;
    },
    addNote(input) { return action({ action: "add_note", ...input }, "Note could not be added."); },
    closeBill(input) { return action({ action: "close", ...input }, "Bill could not be closed."); },
    reopenBill(input) { return action({ action: "reopen", ...input }, "Bill could not be reopened."); },
    postPayment(input) { return action({ action: "post_payment", penaltyAmount: 0, interestAmount: 0, ...input, checkNumber: input.checkNumber ?? "" }, "Payment could not be posted."); },
    submitSecondReview(input) { return action({ action: "second_review", ...input }, "Second Review could not be submitted."); },
    resubmitBill(input) { return action({ action: "resubmit", ...input }, "Bill could not be resubmitted."); },
    submitNewBill(input) { return action({ action: "submit_new_bill", ...input }, "The new bill could not be submitted."); },
    sendDuplicateBill(input) { return action({ action: "send_duplicate", ...input }, "Duplicate bill could not be sent."); },
    reportBillStatus(input) { return action({ action: "report_bill_status", ...input }, "Bill status could not be reported."); },
    simulateSandbox,
  };
}

/**
 * Browser-safe client for reference data needed before a bill exists.
 *
 * Partners can pass the same short-lived session provider used by the submitted-bill
 * lifecycle without inventing a draft bill or exposing payer directory credentials.
 */
export function createBillReferenceClient(
  options: BillReferenceClientOptions = {},
): BillReferenceClient {
  const lifecycle = createBillLifecycleClient({
    ...options,
    billId: "pre-submission-reference-data",
  });
  return {
    listMedicalProviderNetworks: lifecycle.listMedicalProviderNetworks,
    listClaimsAdministrators: lifecycle.listClaimsAdministrators,
    searchClaimsAdministrators: lifecycle.searchClaimsAdministrators,
    getClaimsAdministratorDirectory: lifecycle.getClaimsAdministratorDirectory,
    searchDiagnosisCodes: lifecycle.searchDiagnosisCodes,
    searchProcedureCodes: lifecycle.searchProcedureCodes,
    quoteFee: lifecycle.quoteFee,
    quoteClaimFees: lifecycle.quoteClaimFees,
    quoteEncounter: lifecycle.quoteEncounter,
    lookupPostalCode: lifecycle.lookupPostalCode,
    getDeliveryPreview: lifecycle.getDeliveryPreview,
    clearSession: lifecycle.clearSession,
  };
}

// ---------------------------------------------------------------------------
// Organization profile client (INC-1470): backs the embeddable
// OrganizationOnboarding / BillingSettings components. Requires a browser
// session minted with organization:manage for administrative operations.
// Read-only saved billing profiles need only an org-wide bills:create session.

/** Convert masked profile state to a write without clearing an unchanged saved SSN. */
export function organizationTaxIdWrite<T extends { taxId?: string; taxIdType?: "EIN" | "SSN"; taxIdLast4?: string; taxIdConfigured?: boolean }>(value: T): Omit<T, "taxIdLast4" | "taxIdConfigured"> {
  const input = { ...value };
  delete input.taxIdLast4;
  delete input.taxIdConfigured;
  if (value.taxIdType === "SSN" && value.taxIdConfigured && !value.taxId?.trim()) delete input.taxId;
  return input;
}

export type OrganizationPracticeIdentity = {
  name?: string; legalName?: string; taxId?: string; npi?: string;
  taxIdType?: "EIN" | "SSN";
  /** Read-only metadata. Saved SSNs are never returned in taxId. */
  taxIdLast4?: string; taxIdConfigured?: boolean;
  phone?: string; email?: string; website?: string;
};

export type OrganizationBillingProviderInput = {
  id?: string; externalId?: string; name: string; taxId?: string; npi: string;
  taxIdType?: "EIN" | "SSN";
  /** Read-only metadata; omit taxId on updates to preserve a saved SSN. */
  taxIdLast4?: string; taxIdConfigured?: boolean;
  billType?: "Professional" | "Institutional"; phone?: string;
  billingStreet?: string; billingCity?: string; billingState?: string; billingZip?: string;
};

export type OrganizationLocationInput = {
  billingProviderId?: string;
  id?: string; externalId?: string; name: string; street: string; city: string;
  state: string; zip: string; nickname?: string; posCode?: string;
  isPrimary?: boolean; active?: boolean;
  /** Read-only: the practice contact saved for authorization requests sent from this location. */
  contact?: { name?: string; phone?: string; fax?: string; email?: string };
};

export type OrganizationRenderingProviderInput = {
  id?: string; externalId?: string; name: string; npi: string; specialty?: string;
  taxonomy?: string; licenseNumber?: string; licenseState?: string;
  isQME?: boolean; isAME?: boolean; email?: string; active?: boolean;
};

export type OrganizationChecklistItem = { id: string; label: string; complete: boolean };

export type OrganizationProfileData = {
  organizationId: string;
  practiceIdentity: OrganizationPracticeIdentity;
  billingProviders: Array<OrganizationBillingProviderInput & { id: string }>;
  /** Optional for compatibility with older organization-profile responses. */
  renderingProviders?: Array<OrganizationRenderingProviderInput & { id: string }>;
  locations: Array<OrganizationLocationInput & { id: string }>;
  w9: { filename: string; addDate: string; taxYear?: number } | null;
  onboarding: { status: string | null; complete: boolean; checklist: OrganizationChecklistItem[] };
};

export type OrganizationClientOptions = {
  sessionEndpoint?: string;
  getSession?: BillLifecycleSessionProvider;
  apiBaseUrl?: string;
  fetch?: typeof globalThis.fetch;
};

export type OrganizationTeamRole = "admin" | "manager" | "biller" | "payments" | "viewer";
export type OrganizationTeamMember = {
  id: string; email: string; name: string; role: string; active: boolean;
  createdAt: string; canManage: boolean;
};
export type OrganizationTeam = {
  members: OrganizationTeamMember[];
  roles: Array<{ id: OrganizationTeamRole; label: string; permissions: string[] }>;
  capabilities: { canManage: boolean; canAdd: boolean };
  identityDomain: "mindbill";
};
export type OrganizationClaimsAdministratorInput = {
  name: string; fax?: string; email?: string; mailingAddress?: string; notes?: string;
};
export type OrganizationClaimsAdministrator = {
  id: string; name: string; fax: string | null; email: string | null;
  mailingAddress: string | null; notes: string | null; submissionMethod: string;
  active: boolean; createdAt: string;
};

export type OrganizationClient = {
  /** Requires explicit team:manage; organization:manage alone does not grant team access. */
  getTeam: () => Promise<OrganizationTeam>;
  updateTeamMember: (id: string, input: { role?: OrganizationTeamRole; active?: boolean }) => Promise<OrganizationTeamMember>;
  getClaimsAdministrators: () => Promise<OrganizationClaimsAdministrator[]>;
  createClaimsAdministrator: (input: OrganizationClaimsAdministratorInput) => Promise<OrganizationClaimsAdministrator>;
  updateClaimsAdministrator: (id: string, input: OrganizationClaimsAdministratorInput) => Promise<OrganizationClaimsAdministrator>;
  deleteClaimsAdministrator: (id: string) => Promise<{ id: string; deleted: true }>;

  getOrganization: () => Promise<OrganizationProfileData>;
  /** Masked saved profiles for bill entry; requires org-wide bills:create. */
  getBillingProfile: () => Promise<OrganizationProfileData>;
  saveBillingProfile: (input: {
    practiceIdentity?: OrganizationPracticeIdentity;
    billingProviders?: OrganizationBillingProviderInput[];
    renderingProviders?: OrganizationRenderingProviderInput[];
  }) => Promise<OrganizationProfileData>;
  saveLocations: (locations: OrganizationLocationInput[]) => Promise<OrganizationProfileData>;
  saveW9: (input: { filename: string; contentBase64: string; taxYear?: number }) => Promise<OrganizationProfileData>;
};

export function createOrganizationClient({
  sessionEndpoint = DEFAULT_SESSION_ENDPOINT,
  getSession,
  apiBaseUrl = DEFAULT_API_BASE_URL,
  fetch: fetchOverride,
}: OrganizationClientOptions = {}): OrganizationClient {
  const fetcher = fetchOverride ?? globalThis.fetch;
  if (typeof fetcher !== "function") throw new Error("A Fetch API implementation is required.");
  let session: BillLifecycleSession | null = null;

  const mintSession = async (force = false): Promise<BillLifecycleSession> => {
    if (!force && isSessionFresh(session)) return session as BillLifecycleSession;
    const pending = getSession
      ? getSession({ signal: new AbortController().signal })
      : fetcher(sessionEndpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({}),
      }).then(async (response) => {
        if (!response.ok) throw await responseError(response, "The organization session could not be created.");
        return response.json();
      });
    session = normalizeSession(await Promise.resolve(pending));
    return session;
  };

  const request = async <T = OrganizationProfileData>(path: string, init: RequestInit = {}): Promise<T> => {
    let current = await mintSession();
    const perform = (active: BillLifecycleSession) => {
      const base = (active.apiBaseUrl ?? apiBaseUrl).replace(/\/$/, "");
      const headers = new Headers(init.headers);
      headers.set("authorization", `Bearer ${active.token}`);
      if (init.body) headers.set("content-type", "application/json");
      return fetcher(`${base}${path}`, { ...init, headers });
    };
    let response = await perform(current);
    if (response.status === 401) {
      current = await mintSession(true);
      response = await perform(current);
    }
    if (!response.ok) throw await responseError(response, "The organization request failed.");
    const body = (await response.json()) as { data?: T };
    if (!body.data || typeof body.data !== "object") {
      throw new Error("The organization response was invalid.");
    }
    return body.data;
  };

  return {
    getTeam: () => request<OrganizationTeam>("/partner/v2/organization/team"),
    updateTeamMember: (id, input) => request<OrganizationTeamMember>(`/partner/v2/organization/team/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(input) }),
    getClaimsAdministrators: () => request<OrganizationClaimsAdministrator[]>("/partner/v2/organization/claims-administrators"),
    createClaimsAdministrator: (input) => request<OrganizationClaimsAdministrator>("/partner/v2/organization/claims-administrators", { method: "POST", body: JSON.stringify(input) }),
    updateClaimsAdministrator: (id, input) => request<OrganizationClaimsAdministrator>(`/partner/v2/organization/claims-administrators/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(input) }),
    deleteClaimsAdministrator: (id) => request<{ id: string; deleted: true }>(`/partner/v2/organization/claims-administrators/${encodeURIComponent(id)}`, { method: "DELETE" }),
    getOrganization: () => request("/partner/v2/organization"),
    getBillingProfile: () => request("/partner/v2/organization/billing-profile"),
    saveBillingProfile: (input) =>
      request("/partner/v2/organization/billing-profile", { method: "PUT", body: JSON.stringify({
        ...input,
        ...(input.practiceIdentity ? { practiceIdentity: organizationTaxIdWrite(input.practiceIdentity) } : {}),
        ...(input.billingProviders ? { billingProviders: input.billingProviders.map(organizationTaxIdWrite) } : {}),
      }) }),
    saveLocations: (locations) =>
      request("/partner/v2/organization/locations", { method: "PUT", body: JSON.stringify({ locations }) }),
    saveW9: (input) =>
      request("/partner/v2/organization/w9", { method: "PUT", body: JSON.stringify(input) }),
  };
}

// ---------------------------------------------------------------------------
// Bill Tasks dashboard: a pure, framework-neutral
// aggregation shared by the React and Angular components. Hosts flatten their
// own work items and get back per-section rows bucketed by age in days.

export type BillTasksAgingBucket = {
  id: string;
  label: string;
  /** Exclusive lower bound in days. The first bucket also admits day 0. */
  minDays: number;
  /** Inclusive upper bound in days; null means unbounded. */
  maxDays: number | null;
};

export const BILL_TASKS_AGING_BUCKETS: BillTasksAgingBucket[] = [
  { id: "1-30", label: "1-30 Days Ago", minDays: 0, maxDays: 30 },
  { id: "31-60", label: "31-60 Days Ago", minDays: 30, maxDays: 60 },
  { id: "61-90", label: "61-90 Days Ago", minDays: 60, maxDays: 90 },
  { id: "91-180", label: "91-180 Days Ago", minDays: 90, maxDays: 180 },
  { id: "181+", label: "181+ Days Ago", minDays: 180, maxDays: null },
];

export type BillTasksDashboardItem = {
  sectionId: string;
  rowId: string;
  rowLabel: string;
  ageDays: number;
  balanceDue?: number;
  /** Opaque bill reference echoed back in click-through payloads. */
  ref?: string;
};

export type BillTasksDashboardRow = {
  id: string;
  label: string;
  /** One count per aging bucket, in bucket order. */
  counts: number[];
  total: number;
  /** Summed item balanceDue per bucket; optional for older count-only payloads. */
  balances?: number[];
  balanceTotal?: number;
  /** Collected item refs per aging bucket, in bucket order. */
  refs: string[][];
};

export type BillTasksDashboardTone = "violet" | "red" | "blue" | "green" | "amber" | "neutral";

export type BillTasksDashboardSectionInput = {
  id: string;
  label: string;
  /** Shown under the section label as "by {agingBasisLabel}". */
  agingBasisLabel: string;
  tone: BillTasksDashboardTone;
};

export type BillTasksDashboardSection = BillTasksDashboardSectionInput & {
  rows: BillTasksDashboardRow[];
  /** Per-bucket totals across the section's rows. */
  totals: number[];
  total: number;
  balanceTotals?: number[];
  balanceTotal?: number;
  empty: boolean;
};

export type BillTasksDashboardData = {
  sections: BillTasksDashboardSection[];
  grandTotals: number[];
  grandTotal: number;
  /** Work-item sums, not deduplicated financial A/R across task categories. */
  grandBalanceTotals?: number[];
  grandBalanceTotal?: number;
};

/**
 * Bucket index for an item age: a bucket admits `minDays < ageDays <= maxDays`
 * (`maxDays: null` = unbounded) and the first bucket also admits day 0.
 * Ages beyond every bucket clamp into the last bucket.
 */
export function billTasksAgingBucketIndex(
  ageDays: number,
  buckets: BillTasksAgingBucket[] = BILL_TASKS_AGING_BUCKETS,
): number {
  const days = Math.max(0, Math.floor(ageDays));
  const index = buckets.findIndex((bucket, position) =>
    (bucket.maxDays === null || days <= bucket.maxDays)
    && (days > bucket.minDays || position === 0));
  return index === -1 ? buckets.length - 1 : index;
}

/**
 * Aggregates flat work items into the Bill Tasks dashboard.
 * Sections render in the given order even when empty; rows appear in
 * first-seen item order within their section.
 */
export function buildBillTasksDashboard(
  items: BillTasksDashboardItem[],
  sections: BillTasksDashboardSectionInput[],
  buckets: BillTasksAgingBucket[] = BILL_TASKS_AGING_BUCKETS,
): BillTasksDashboardData {
  const zeros = () => buckets.map(() => 0);
  const built = sections.map((section): BillTasksDashboardSection => ({
    ...section,
    rows: [],
    totals: zeros(),
    total: 0,
    balanceTotals: zeros(),
    balanceTotal: 0,
    empty: true,
  }));
  const sectionById = new Map(built.map((section) => [section.id, section]));
  const rowByKey = new Map<string, BillTasksDashboardRow>();
  const grandTotals = zeros();
  let grandTotal = 0;
  const grandBalanceTotals = zeros();
  let grandBalanceTotal = 0;

  for (const item of items) {
    const section = sectionById.get(item.sectionId);
    if (!section) continue;
    const key = `${item.sectionId}\u0000${item.rowId}`;
    let row = rowByKey.get(key);
    if (!row) {
      row = {
        id: item.rowId,
        label: item.rowLabel,
        counts: zeros(),
        total: 0,
        balances: zeros(),
        balanceTotal: 0,
        refs: buckets.map(() => []),
      };
      rowByKey.set(key, row);
      section.rows.push(row);
      section.empty = false;
    }
    const bucketIndex = billTasksAgingBucketIndex(item.ageDays, buckets);
    const balance = Number.isFinite(item.balanceDue) ? item.balanceDue! : 0;
    row.counts[bucketIndex] = (row.counts[bucketIndex] ?? 0) + 1;
    row.total += 1;
    row.balances![bucketIndex] = (row.balances![bucketIndex] ?? 0) + balance;
    row.balanceTotal = (row.balanceTotal ?? 0) + balance;
    if (item.ref) row.refs[bucketIndex]?.push(item.ref);
    section.totals[bucketIndex] = (section.totals[bucketIndex] ?? 0) + 1;
    section.total += 1;
    section.balanceTotals![bucketIndex] = (section.balanceTotals![bucketIndex] ?? 0) + balance;
    section.balanceTotal = (section.balanceTotal ?? 0) + balance;
    grandTotals[bucketIndex] = (grandTotals[bucketIndex] ?? 0) + 1;
    grandTotal += 1;
    grandBalanceTotals[bucketIndex] = (grandBalanceTotals[bucketIndex] ?? 0) + balance;
    grandBalanceTotal += balance;
  }

  return { sections: built, grandTotals, grandTotal, grandBalanceTotals, grandBalanceTotal };
}


/** Contacts snapshotted on a request and included in the reviewed form. */
export type RfaContact = {
  name?: string; contactName?: string; address?: string; city?: string; state?: string;
  zip?: string; phone?: string; fax?: string; email?: string;
};
export type RfaDeliveryPreviewInput = { documentIds: string[]; channel: "fax" | "email" | "download"; to?: string; recipientName?: string; message?: string };
export type RfaDeliveryPreview = { packetId: string; sha256: string; contentRevision: number };
export type RfaSubmitInput = Omit<RfaDeliveryPreview, "contentRevision"> & { channel: "fax" | "email"; to: string; recipientName?: string; message?: string; nonBusinessDates?: string[] };
/** An unsigned RFA preparation draft. Signing and sending are separate operations. */
export type RfaCreateDraftInput = {
  claimId: string; patientId: string; renderingProviderId: string; employeeName: string; providerName: string;
  externalId?: string; claimsAdminId?: string; writtenConfirmation?: boolean;
  requestingPractice?: RfaContact | null; authorizationContact?: RfaContact | null;
  requestType?: "new" | "resubmission_material_change" | "oral_authorization_confirmation";
  reviewType?: "prospective" | "concurrent" | "retrospective"; expedited?: boolean;
  placeOfServiceCode?: string; providerNpi?: string; providerPhone?: string; providerFax?: string;
  claimNumber?: string; dateOfInjury?: string; rationale?: string; materialChange?: string;
  items: Array<{ externalId?: string; diagnosisCode: string; diagnosisDescription?: string; serviceDescription: string; procedureCode?: string;
    quantity?: number; units?: number; frequency?: string; duration?: string; requestedFrom?: string; requestedTo?: string; metadata?: Record<string, unknown> }>;
  metadata?: Record<string, unknown>;
};
/** Full replacement of editable request content; retained service rows keep their IDs. */
export type RfaUpdateDraftInput = Omit<RfaCreateDraftInput, "externalId" | "claimId" | "patientId" | "renderingProviderId" | "items"> & {
  expectedRevision: number;
  items: Array<Omit<RfaCreateDraftInput["items"][number], "externalId"> & { id?: string }>;
};
export type RfaRecord = {
  /** Optional human-readable references supplied by the source system. */
  displayReference?: string;
  claimsAdminName?: string;
  injuryDescription?: string;
  /** Read-only current contacts used by the form when no saved contact exists. */
  formContactDefaults?: { requestingPractice?: RfaContact; authorizationContact?: RfaContact };
  contentRevision: number; id: string; claimId: string; patientId: string; renderingProviderId: string; claimsAdminId: string | null;
  /** Employee and physician fields printed on the DWC Form RFA. */
  employeeDateOfBirth?: string | null; employer?: string | null; providerSpecialty?: string | null;
  employeeName: string; providerName: string; claimNumber: string | null; status: string;
  /** Delivery/review lifecycle, separate from the clinical status and item outcomes. */
  lifecycleStatus?: import("./rfa-status").RfaLifecycleStatus;
  requestType?: RfaCreateDraftInput["requestType"]; writtenConfirmation?: boolean;
  requestingPractice?: RfaContact | null; authorizationContact?: RfaContact | null;
  placeOfServiceCode?: string | null; providerNpi?: string | null; providerPhone?: string | null; providerFax?: string | null;
  dateOfInjury?: string | null; rationale?: string | null; materialChange?: string | null; metadata?: Record<string, unknown>;
  reviewType: string; expedited: boolean; createdAt: string | null; updatedAt: string | null;
  signedAt: string | null; submittedAt: string | null; receivedAt: string | null;
  decisionDueAt: string | null; decisionDeadlineBasis: string | null;
  incompleteReason: string | null; deferredReason: string | null; closedReason: string | null;
  readiness: { ready: boolean; missing: string[] };
  items: Array<{ id: string; diagnosisCode: string; diagnosisDescription?: string; serviceDescription: string; procedureCode: string | null;
    frequency?: string | null; duration?: string | null; requestedFrom?: string | null; requestedTo?: string | null; metadata?: Record<string, unknown>;
    outcome: string; quantity: number | null; units: number | null; authorizationNumber: string | null; decisionReason: string | null;
    decisionClosure?: import("./rfa-lifecycle").RfaTreatmentClosure | null;
    currentDecisionEventId?: string | null; currentResponseDocumentId?: string | null; currentImrDocumentId?: string | null;
    authorizedProcedureCode?: string | null; authorizedQuantity?: number | null; authorizedUnits?: number | null;
    effectiveFrom?: string | null; effectiveTo?: string | null; decidedAt?: string | null; reviewerName?: string | null; reviewerPhone?: string | null }>;
  /** `source`: where the file came from (upload, mindbill, fax, bill, injury, library). `internal`: a working file such as a signing preview, not part of the request. */
  documents: Array<{ id: string; documentType: string; filename: string; contentUrl: string; contentRevision: number | null; createdAt: string | null; source?: string; internal?: boolean }>;
  transmissions: Array<{ id: string; purpose?: string; direction: string; channel: string; status: string; destination: string | null;
    occurredAt: string | null; receivedAt: string | null; proofDocumentId: string | null; providerMessageId: string | null;
    /** sentOutsideMindBill is true for a send the office recorded itself; such a send can be undone. */
    metadata?: Record<string, unknown> }>;
  informationRequests: Array<{ id: string; requestText: string; requestedAt: string | null; dueAt: string | null; respondedAt: string | null }>;
  events: Array<{ id: string; type: string; occurredAt: string | null; text?: string }>;
};
export type RfaAgingBucket = "0_5" | "6_14" | "15_30" | "31_plus";
export type RfaAgingSummary = { byBucket: Record<RfaAgingBucket, number>; byLifecycleStatus: Record<import("./rfa-status").RfaLifecycleStatus, Record<RfaAgingBucket, number>> };
export type RfaListQuery = { agingBucket?: RfaAgingBucket; patientId?: string; search?: string; sortBy?: "createdAt" | "employeeName" | "providerName" | "submittedAt" | "status" | "lifecycleStatus"; sortDirection?: "asc" | "desc"; claimId?: string; renderingProviderId?: string; status?: string; lifecycleStatus?: import("./rfa-status").RfaLifecycleStatus; /** Sent requests awaiting a decision whose deadline has passed, or falls within the next seven days. */ decisionDue?: "overdue" | "next_7_days"; createdFrom?: string; createdTo?: string; limit?: number; cursor?: string };
export type RfaListResult = { data: RfaRecord[]; nextCursor: string | null; summary: { total: number; byStatus: Record<string, number>; aging?: RfaAgingSummary; byLifecycleStatus?: Partial<Record<import("./rfa-status").RfaLifecycleStatus, number>> } };
export type RfaSigningPreviewInput = { billingProviderId?: string; diagnosisDescriptions: Record<string, string> };
export type RfaSigningPreview = { id: string; contentHash: string; contentRevision: number; renderingProviderId: string; previewDocumentId: string; expiresAt: string };
export type RfaSignInput = { snapshotId: string; contentHash: string; renderingProviderId: string; physicianAuthorized: true; actorReference: string };
export type RfaFaxInput = { to: string; documentIds: string[]; nonBusinessDates?: string[] };
export type RfaUploadDocumentInput = { file: File; documentType: "clinical_report" | "supporting_record" | "ur_response" | "imr_form" | "other"; contentRevision: number };
/** `employeeFormName`, `dateOfBirth` and `employer` mirror the DWC Form RFA employee block. Date of birth and employer are returned only when the query names this `claimId`. */
export type RfaCreationClaim = { claimId: string; patientId: string; employeeName: string; employeeFormName?: string; dateOfBirth?: string; employer?: string; claimNumber?: string; dateOfInjury?: string; claimsAdminId?: string; diagnosisCodes?: string[] };
export type RfaCreationProvider = { id: string; name: string; npi?: string; specialty?: string };
/** `returnFax` and `returnEmail` are the organization's MindBill-provided fax line and monitored e-mail address for utilization review responses, when set up. */
export type RfaCreationContext = { claims: RfaCreationClaim[]; renderingProviders: RfaCreationProvider[]; nextCursor: string | null; renderingProvidersNextCursor: string | null; returnFax?: string; returnEmail?: string };
export type RfaCreationContextQuery = { search?: string; cursor?: string; providerSearch?: string; providerCursor?: string; limit?: number; claimId?: string; renderingProviderId?: string };
export type RfaProvisionClaimInput = {
  patient: Omit<BrowserBillCreateInput["patient"], "id" | "externalId"> & { externalId: string };
  claim: Omit<BrowserBillCreateInput["claim"], "id" | "externalId"> & { externalId: string };
};
export type RfaProvisionClaimResult = { patientId: string; claimId: string; patientExternalId: string; claimExternalId: string; created: { patient: boolean; claim: boolean } };
export type RfaClient = {
  provisionClaim?: (input: RfaProvisionClaimInput, options?: { idempotencyKey?: string }) => Promise<RfaProvisionClaimResult>;
  searchClaimsAdministrators?: (query: string, claimNumber?: string) => Promise<BillReviewPayer[]>;
  getClaimsAdministratorDirectory?: (id: string, injuryState?: string) => Promise<BillClaimsAdministratorDirectory>;
  saveProviderSignature?: (providerId: string, input: { contentBase64: string; physicianAuthorized: true; actorReference: string }, options?: { idempotencyKey?: string }) => Promise<{ providerId: string; signatureConfigured: true }>;

  getCreationContext: (query?: RfaCreationContextQuery) => Promise<RfaCreationContext>;
  list: (query?: RfaListQuery) => Promise<RfaListResult>;
  get: (rfaId: string) => Promise<RfaRecord>;
  createDraft: (draft: RfaCreateDraftInput, idempotencyKey: string) => Promise<RfaRecord>;
  updateDraft: (rfaId: string, draft: RfaUpdateDraftInput, idempotencyKey: string) => Promise<RfaRecord>;
  getDocument: (rfaId: string, documentId: string) => Promise<Blob>;
  uploadDocument: (rfaId: string, input: RfaUploadDocumentInput, idempotencyKey: string) => Promise<RfaRecord>;
  /** Remove an uploaded supporting document from an unsent request. The server refuses sent requests and required documents. */
  removeDocument?: (rfaId: string, documentId: string, idempotencyKey: string) => Promise<RfaRecord>;
  prepareSigning: (rfaId: string, input: RfaSigningPreviewInput, idempotencyKey: string) => Promise<RfaSigningPreview>;
  sign: (rfaId: string, input: RfaSignInput, idempotencyKey: string) => Promise<RfaRecord>;
  prepareDelivery: (rfaId: string, input: RfaDeliveryPreviewInput, key?: string) => Promise<RfaDeliveryPreview>;
  getPacket: (rfaId: string, packetId: string) => Promise<Blob>;
  submit: (rfaId: string, input: RfaSubmitInput, idempotencyKey: string) => Promise<RfaRecord>;
  previewPacket: (rfaId: string, documentIds: string[]) => Promise<Blob>;
  sendFax: (rfaId: string, input: RfaFaxInput, idempotencyKey: string) => Promise<RfaRecord>;
  refreshFaxes: (rfaId: string, idempotencyKey: string) => Promise<RfaRecord>;
  /** The DWC Form RFA as it stands (signed form or unsigned preview), or with `packet` the whole fax packet. Read-only. */
  previewForm?: (rfaId: string, options?: { packet?: boolean }) => Promise<Blob>;
  /** The unsigned DWC Form RFA filled from an unsaved draft. Saves nothing. */
  previewDraft?: (draft: RfaDraftPreviewInput) => Promise<Blob>;
  /** Proposed decisions read from an attached utilization review response, for a person to check. Saves nothing. */
  readResponse?: (rfaId: string, documentId: string) => Promise<RfaResponseReadingResult>;
  /** The organization's saved treatment templates, optionally for one practice. */
  listTemplates?: (billingProviderId?: string) => Promise<RfaTemplateRecord[]>;
  /** Creates a template, or updates one by id (`active: false` retires it). */
  saveTemplate?: (input: RfaTemplateSaveInput, idempotencyKey?: string) => Promise<RfaTemplateRecord>;
  /** CSV of the filtered requests, one row per treatment. */
  exportCsv?: (query?: Omit<RfaListQuery, "limit" | "cursor">) => Promise<Blob>;
};
/** An unsaved request as typed so far; nothing is required for a preview. */
export type RfaDraftPreviewInput = {
  claimId?: string | null; patientId?: string | null; renderingProviderId?: string | null; claimsAdminId?: string | null;
  employeeName?: string | null; providerName?: string | null; providerNpi?: string | null; providerPhone?: string | null; providerFax?: string | null;
  claimNumber?: string | null; dateOfInjury?: string | null; rationale?: string | null; materialChange?: string | null;
  requestType?: string | null; expedited?: boolean; writtenConfirmation?: boolean;
  requestingPractice?: Partial<Record<"name" | "contactName" | "address" | "city" | "state" | "zip" | "phone" | "fax" | "email", string | null>> | null;
  authorizationContact?: Partial<Record<"name" | "contactName" | "address" | "city" | "state" | "zip" | "phone" | "fax" | "email", string | null>> | null;
  items?: Array<{ diagnosisCode?: string | null; diagnosisDescription?: string | null; serviceDescription?: string | null; procedureCode?: string | null;
    frequency?: string | null; duration?: string | null; requestedFrom?: string | null; requestedTo?: string | null; quantity?: number | null; units?: number | null;
    metadata?: Record<string, unknown> | null }>;
};
export type RfaResponseReadingResult = {
  status: "read" | "unreadable";
  reason?: "no_text" | "model_unavailable" | "model_answer_invalid";
  decidedAt?: string;
  items: Array<{ itemId: string; outcome: "approved" | "modified" | "denied" | "not_addressed"; confident: boolean;
    authorizationNumber?: string; authorizedProcedureCode?: string; authorizedQuantity?: number; authorizedUnits?: number;
    effectiveFrom?: string; effectiveTo?: string; decisionReason?: string; reviewerName?: string; reviewerPhone?: string }>;
};
export type RfaTemplateItemRecord = { externalId: string; serviceDescription: string; procedureCode?: string; quantity?: number; units?: number; frequency?: string; duration?: string; otherInformation?: string };
export type RfaTemplateRecord = { id: string; name: string; billingProviderId: string; renderingProviderId: string | null; active: boolean; items: RfaTemplateItemRecord[] };
export type RfaTemplateSaveInput = { id?: string; name: string; billingProviderId: string; renderingProviderId?: string | null; active?: boolean; items: RfaTemplateItemRecord[] };
/** Uses short-lived browser credentials. Never pass a partner API key. */
export function createRfaClient({ sessionEndpoint = DEFAULT_SESSION_ENDPOINT, getSession, apiBaseUrl = DEFAULT_API_BASE_URL, fetch: fetchOverride }: OrganizationClientOptions = {}): RfaClient {
  const fetcher = fetchOverride ?? globalThis.fetch;
  let session: BillLifecycleSession | null = null;
  let pending: Promise<BillLifecycleSession> | null = null;
  const mint = (force = false): Promise<BillLifecycleSession> => {
    if (!force && isSessionFresh(session)) return Promise.resolve(session as BillLifecycleSession);
    if (pending) return pending;
    pending = (async () => {
      const value = getSession ? await getSession({ signal: new AbortController().signal }) : await (async () => {
        const response = await fetcher(sessionEndpoint, { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: "{}" });
        if (!response.ok) throw await responseError(response, "The RFA session could not be created.");
        return response.json();
      })();
      session = normalizeSession(value); return session;
    })().finally(() => { pending = null; });
    return pending;
  };
  const request = async (path: string, init: RequestInit = {}, browser = false, resource = "rfas") => {
    const perform = (active: BillLifecycleSession) => {
      const headers = new Headers(init.headers); headers.set("authorization", `Bearer ${active.token}`);
      if (init.body && !(init.body instanceof FormData)) headers.set("content-type", "application/json");
      return fetcher(`${(active.apiBaseUrl ?? apiBaseUrl).replace(/\/$/, "")}/partner/v2/${browser ? "browser/" : ""}${resource}${path}`, { ...init, headers });
    };
    let response = await perform(await mint());
    if (response.status === 401) response = await perform(await mint(true));
    if (!response.ok) throw await responseError(response, "The RFA request failed.");
    return response;
  };
  const record = async (path: string, init?: RequestInit): Promise<RfaRecord> => {
    const body = await (await request(path, init)).json() as { data?: RfaRecord };
    if (!body.data?.id) throw new Error("The RFA response was invalid.");
    return body.data;
  };
  const mutation = (body: unknown, key: string): RequestInit => {
    if (!key.trim()) throw new Error("An idempotency key is required.");
    return { method: "POST", headers: { "idempotency-key": key }, body: JSON.stringify(body) };
  };
  const path = (id: string, suffix: string) => `/${encodeURIComponent(id)}/${suffix}`;
  return {
    provisionClaim: async (input, options) => {
      const response = await request("", mutation(input, options?.idempotencyKey ?? globalThis.crypto.randomUUID()), false, "claims");
      const body = await response.json() as RfaProvisionClaimResult;
      if (!body.patientId || !body.claimId) throw new Error("The saved patient and injury response was invalid.");
      return body;
    },
    searchClaimsAdministrators: (query, claimNumber) => createBillReferenceClient({ sessionEndpoint, ...(getSession ? { getSession } : {}), apiBaseUrl, ...(fetchOverride ? { fetch: fetchOverride } : {}) }).searchClaimsAdministrators(query, claimNumber),
    getClaimsAdministratorDirectory: (id, injuryState) => createBillReferenceClient({ sessionEndpoint, ...(getSession ? { getSession } : {}), apiBaseUrl, ...(fetchOverride ? { fetch: fetchOverride } : {}) }).getClaimsAdministratorDirectory(id, injuryState),
    saveProviderSignature: async (providerId, input, options) => {
      const response = await request(`/providers/${encodeURIComponent(providerId)}/signature`, mutation(input, options?.idempotencyKey ?? globalThis.crypto.randomUUID()));
      const body = await response.json() as { data: { providerId: string; signatureConfigured: true } };
      if (body.data?.signatureConfigured !== true) throw new Error("The physician signature could not be confirmed.");
      return body.data;
    },
    getCreationContext: async (query = {}) => {
      const params = new URLSearchParams();
      for (const [name, value] of Object.entries(query)) if (value !== undefined && value !== "") params.set(name, String(value));
      const body = await (await request(`/creation-context${params.size ? `?${params}` : ""}`)).json() as { data?: RfaCreationContext };
      if (!Array.isArray(body.data?.claims) || !Array.isArray(body.data?.renderingProviders)) throw new Error("The RFA creation context was invalid.");
      return body.data;
    },
    list: async (query = {}) => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(query)) if (value !== undefined && value !== "") params.set(key, String(value));
      const body = await (await request(params.size ? `?${params}` : "")).json() as RfaListResult;
      if (!Array.isArray(body.data) || !body.summary) throw new Error("The RFA list response was invalid.");
      return body;
    },
    get: (id) => record(`/${encodeURIComponent(id)}`),
    createDraft: (draft, idempotencyKey) => {
      if (!idempotencyKey.trim()) return Promise.reject(new Error("An idempotency key is required."));
      const unsigned = { ...draft } as RfaCreateDraftInput & { signedAt?: unknown }; delete unsigned.signedAt;
      unsigned.items = draft.items.map(item => { const copy = { ...item } as typeof item & { id?: unknown }; delete copy.id; return copy; });
      return record("", { method: "POST", headers: { "idempotency-key": idempotencyKey }, body: JSON.stringify(unsigned) });
    },
    updateDraft: (id, draft, key) => {
      if (!Number.isInteger(draft.expectedRevision) || draft.expectedRevision < 1) return Promise.reject(new Error("A positive expected content revision is required."));
      const body = { ...draft } as RfaUpdateDraftInput & Record<string, unknown>;
      for (const field of ["externalId", "claimId", "patientId", "renderingProviderId", "signedAt"]) delete body[field];
      body.items = draft.items.map(item => { const copy = { ...item } as typeof item & { externalId?: unknown }; delete copy.externalId; return copy; });
      return record(path(id, "draft"), { ...mutation(body, key), method: "PATCH" });
    },
    uploadDocument: (id, input, key) => {
      if (!key.trim()) return Promise.reject(new Error("An idempotency key is required."));
      if (input.file.size < 1 || input.file.size > 25 * 1024 * 1024) return Promise.reject(new Error("Choose a PDF up to 25 MB."));
      const body = new FormData(); body.set("file", input.file); body.set("documentType", input.documentType); body.set("contentRevision", String(input.contentRevision));
      return record(path(id, "documents"), { method: "POST", headers: { "idempotency-key": key }, body });
    },
    prepareSigning: async (id, input, key) => {
      const body = await (await request(path(id, "signing-preview"), mutation(input, key))).json() as { data: RfaSigningPreview };
      if (!body.data?.id || !body.data.contentHash || !body.data.previewDocumentId) throw new Error("The signing preview was invalid.");
      return body.data;
    },
    sign: (id, input, key) => {
      if (input.physicianAuthorized !== true || !input.actorReference.trim()) return Promise.reject(new Error("Physician authorization and signer identity are required."));
      return record(path(id, "sign"), mutation(input, key));
    },
    prepareDelivery: async (id, input, key) => {
      const body = await (await request(path(id, "delivery-preview"), mutation(input, key ?? idempotencyKey()))).json() as { data: RfaDeliveryPreview };
      if (!body.data?.packetId || !body.data.sha256 || !body.data.contentRevision) throw new Error("The delivery preview was invalid.");
      return body.data;
    },
    getPacket: async (id, packetId) => (await request(path(id, `packets/${encodeURIComponent(packetId)}`))).blob(),
    submit: async (id, input, key) => {
      const body = await (await request(path(id, "submit"), mutation(input, key))).json() as { data: { transmissionId: string; packetId: string } };
      if (!body.data?.transmissionId || !body.data.packetId) throw new Error("The submission response was invalid. Refresh before trying again.");
      return record(`/${encodeURIComponent(id)}`);
    },
    previewPacket: async (id, documentIds) => (await request(path(id, "packet"), { method: "POST", body: JSON.stringify({ documentIds }) })).blob(),
    sendFax: (id, input, key) => record(path(id, "fax"), mutation(input, key)),
    refreshFaxes: (id, key) => record(path(id, "fax/refresh"), mutation({}, key)),
    getDocument: async (id, documentId) => (await request(`/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}`)).blob(),
    removeDocument: (id, documentId, key) => {
      if (!key.trim()) return Promise.reject(new Error("An idempotency key is required."));
      return record(path(id, `documents/${encodeURIComponent(documentId)}`), { method: "DELETE", headers: { "idempotency-key": key } });
    },
    previewForm: async (id, options) => (await request(path(id, `form-preview${options?.packet ? "?packet=1" : ""}`))).blob(),
    previewDraft: async (draft) => (await request("/draft-preview", { method: "POST", body: JSON.stringify(draft) })).blob(),
    readResponse: async (id, documentId) => {
      const body = await (await request(path(id, "response-reading"), { method: "POST", body: JSON.stringify({ documentId }) })).json() as { data?: RfaResponseReadingResult };
      if (!body.data || !Array.isArray(body.data.items) || !["read", "unreadable"].includes(body.data.status)) throw new Error("The response reading was invalid.");
      return body.data;
    },
    listTemplates: async (billingProviderId) => {
      const query = billingProviderId ? `?${new URLSearchParams({ billingProviderId })}` : "";
      const body = await (await request(query, {}, false, "rfa-templates")).json() as { data?: RfaTemplateRecord[] };
      if (!Array.isArray(body.data)) throw new Error("The RFA templates response was invalid.");
      return body.data;
    },
    saveTemplate: async (input, key) => {
      const { id, ...template } = input;
      const init = mutation(template, key ?? idempotencyKey());
      const body = await (await request(id ? `/${encodeURIComponent(id)}` : "", id ? { ...init, method: "PATCH" } : init, false, "rfa-templates")).json() as { data?: RfaTemplateRecord };
      if (!body.data?.id) throw new Error("The RFA template response was invalid.");
      return body.data;
    },
    exportCsv: async (query = {}) => {
      const params = new URLSearchParams();
      for (const [name, value] of Object.entries(query)) if (value !== undefined && value !== "") params.set(name, String(value));
      return (await request(`/export${params.size ? `?${params}` : ""}`)).blob();
    },
  };
}

export * from "./rfa-lifecycle";
export * from "./encounter-fees";
export * from "./bill-drafts";
export { createReportAutofillClient } from "./report-autofill";
export type { ReportAutofillClient, ReportAutofillField, ReportAutofillMatch, ReportAutofillResult } from "./report-autofill";

export * from "./rfa-draft-actions";

export * from "./rfa-packets";

export * from "./rfa-status";

export * from "./rfa-records";
